import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import * as THREE from 'three'
import { Canvas } from '@react-three/fiber'
import Scene from './components/Scene'
import { clearContent, frameContent } from './lib/viewportFraming'
import { HDRISwitcherUI } from './components/HDRISwitcher'
import SplatLoadingOverlay from './components/SplatLoadingOverlay'
import ProbeReadoutHUD from './components/ProbeReadoutHUD'
import LightingStatusChip from './components/LightingStatusChip'
import NavmeshStatusChip from './components/NavmeshStatusChip'
import BakeSettingsPanel from './components/BakeSettingsPanel'
import BakeNotifications from './components/BakeNotifications'
import BakePreviewGizmos from './components/BakePreviewGizmos'
import Fp16BlendTarget from './components/Fp16BlendTarget'
import LauncherThumbnailCapturer from './components/LauncherThumbnailCapturer'
import ChatInputBar from './components/ChatInputBar'
import { liveTuneStore } from './lib/liveTuneStore'
import { navSettingsStore } from './lib/navSettingsStore'
import { startBake } from './lib/bakeStatusStore'
import { requestNavBake, isBakeableUrl } from './lib/bakeLightingApi'
import { activeEnvAssetId } from './lib/activeEnvironmentAsset'
import { knownEnvironmentFiles, environmentFileUrl } from './lib/environmentFiles'
import { applyEnvironment, unloadAuthoredEnvironment } from './lib/applyEnvironment'
import { listOrgEnvironments, deleteCharacter, duplicateCharacter, listOgsAssets, fetchAamFile } from './lib/assetManagerApi'
import { isOrgEnvironmentsEnabled } from './lib/featureFlags'
import AvatarModel from './components/AvatarModel'
import HeadModel from './components/HeadModel'
import { BlueprintEditor } from './components/BlueprintEditor'
import AnimTestPanel from './components/AnimTestPanel'
import RigPreviewPanel from './components/RigPreviewPanel'
import AvatarLoadOverlay from './components/AvatarLoadOverlay'
import StreamAvatarModel from './components/StreamAvatarModel'
import LiveTunePanel from './components/LiveTunePanel'
import TextureUpgradeIndicator from './components/TextureUpgradeIndicator'
import HeadTrackIndicator from './components/HeadTrackIndicator'
import WalkHereIndicator from './components/WalkHereIndicator'
// Side-effect import: attaches the Walk-here editor to its menu row
// (setCommandEditor). Nothing renders it directly — ViewportMenu does.
import './components/WalkHereMenuEditor'
import ViewportMenu from './components/ViewportMenu'
import A2FWarningIndicator from './components/A2FWarningIndicator'
import CameraRigNotice from './components/CameraRigNotice'
import OGSAssetWarningIndicator from './components/OGSAssetWarningIndicator'
import { ConvorcherClient } from './network/ConvorcherClient'
import { AudioAnimationBuffer } from './network/AudioAnimationBuffer'
import { EventRuntime } from './lib/EventRuntime'
import { setTransitionActionHandler } from './lib/smTransitionActions'
import { setConstant } from './lib/animVariables'
import { sentenceController } from './lib/SentenceController'
import { audioPlayer } from './network/AudioPlayer'
import { devLog } from './lib/devLog'
import { describeWebSocketClose } from './lib/wsCloseMessage.js'
import { loadBlueprint, loadBlueprintFromDB } from './lib/loadBlueprint'
import { starterTemplateUrl, resolveStarterKind, fetchStarterRegions, applyStarterRegions } from './lib/starterBlueprints'
import { voxyManager } from './lib/VoxyManager'
import { resolveActiveCharacter, loadCharacters, setCurrentCharacterId, clearCharactersCache, isSplatCharacter, isStreamCharacter, isOgsContentForced, applyCharacterPack } from './lib/characterState'
import { isStaleLoadError } from './lib/characterLoadError'
import { checkForRig } from './lib/checkForRig'
import CheckMissingRig from './components/CheckMissingRig'
import LoadingOverlay from './components/LoadingOverlay'
import { subscribeMaterialReload } from './lib/materialReloadBus'
import { dialog } from './lib/dialog'
import { ConnectionStatus } from './components/ConnectionStatus'
import NoCharactersScreen from './components/NoCharactersScreen'
import CharacterLauncher from './components/CharacterLauncher'
import CharacterProperties from './components/CharacterProperties.jsx'
import { useAuth } from './lib/auth/AuthContext.jsx'
import { setCharacterSharing, getShareImpact, assignCharacterOwner, getCharacter } from './lib/assetManagerApi.js'
import { hasOrgAdminRole } from './lib/characterSharing.js'
import BootSplash from './components/BootSplash.jsx'
import { useWindowDrag, useWindowPopOut, WindowResizeGrips, WindowBarButton } from './components/FloatingWindow.jsx'
import { windowStore, placeOnOpen } from './lib/windowStore.js'
import { resolveDeepLinkedCharacterId, readCharacterParam, pinCharacterUrlParam, unpinCharacterUrlParam, readRecentCharacters, pushRecentCharacterId } from './lib/characterSelection.js'
import { probeRigStatuses } from './lib/characterRigStatus.js'
// Feeds the menubar chip's "loading rig · 4.2 / 11.8 MB" readout — the same
// byte counters AvatarLoadOverlay draws its bar from.
import { assetLoadProgress } from './lib/assetLoadProgress.js'
import DialogHost from './components/DialogHost'
import { FORCE_SHOW } from './lib/devServer'
import {
  subscribe as subscribeDraft,
  unsubscribe as unsubscribeDraft,
  getDraft as getCharDraft,
  getStaleBackendDraft,
  applyStaleBackendDraft,
  clearStaleBackendDraft,
  loadDraftFromLS,
  setActiveCharacter,
  hasActiveDraft,
  hasActiveDraftData,
} from './lib/characterAuthoringStore'
import DirtyBadge from './components/DirtyBadge'
import UnsavedChangesModal from './components/UnsavedChangesModal'
import { resetStorageKeys } from './lib/loadBlueprint'
import { clearDraft as clearCharDraft } from './lib/characterAuthoringStore'
import { CustomAssetManager } from './lib/CustomAssetManager'
import { eventGraphState, subscribeEventGraph } from './lib/eventGraphState'
import { resolveSessionConfig } from './lib/sessionCharacter'
import { liveSessionConfigFor } from './lib/sessionConfigStore'
import { headTrackedCamera } from './lib/headTrackedCamera'
import { isNodeTypeReachable, isAnyNodeOfTypeReachable } from './lib/execReachability'
import { loadAllPlugins } from './lib/pluginLoader'
import MultipartOGSModel from './components/MultipartOGSModel'
import AppMenuBar from './components/AppMenuBar'
// The docked shell chrome (build spec): tool rail, outliner, inspector,
// assets dock, console strip. Character-independent by construction.
import { ToolRail, OutlinerPanel, InspectorPanel, GeneratePanel, AssetsDock, ConsoleStrip, ResizeHandle } from './components/DockedShell'
import VersionPanel from './components/versionControl/VersionPanel.jsx'
import { versionPanelStore } from './lib/versionPanelStore'
import MaterialGraphModal from './components/contentBrowser/MaterialGraphModal'
import { materialGraphStore } from './lib/materialGraphStore'
import AgentPanel from './components/agent/AgentPanel.jsx'
import ContentBrowser from './components/contentBrowser/ContentBrowser'
import { buildOutlinerRows, SURFACE_LABELS } from './lib/outlinerTree.js'
import { rigConstrain, setRigConstrain } from './ogs/rig/rigConstrainBlend.js'
import { liveFaceControls, setLiveMouthWeight } from './ogs/rig/liveFaceControls.js'
// The loaded OGS bundle's control space — a splat character's Face rig row
// (ENG-1042). A module ref, passed in like the graph states below.
import { rigBoardSource } from './ogs/rig/rigBoardSource.js'
import { formatDocumentTitle } from './lib/documentTitle.js'
import ViewportDropZone from './components/ViewportDropZone'
import { useCameraRigState } from './hooks/useCameraRigState'
import { shellLayout } from './lib/shellLayout.js'
import { getAdapters } from './lib/transformAdapters'
import { sceneSelection } from './lib/sceneSelection'
import { graphState } from './lib/graphState'
import { faceGraphState } from './lib/faceGraphState'
import { menubarCommands } from './lib/menubarCommands.js'
import { captureAndUploadThumbnail, captureLauncherThumbnailDataUrl, uploadThumbnailDataUrl } from './lib/launcherThumbnail.js'
import { shouldAutoCaptureThumbnail } from './lib/launcherThumbnailCrop.js'
import { thumbnailCapture, getThumbnailCaptureState } from './lib/thumbnailCaptureStore.js'
import ThumbnailCaptureIndicator from './components/ThumbnailCaptureIndicator.jsx'
import { consoleTally } from './lib/consoleTally.js'
import { uiScale } from './lib/uiScale.js'
import { assetInspectorHost } from './lib/assetInspectorHost.js'
import SettingsWorkspace from './components/SettingsWorkspace'
import ShortcutHelp from './components/ShortcutHelp'
import { initPalette } from './lib/palettes/paletteStore'
// Lazy — this pulls in Monaco (large). Only load it when the user opens the
// Conversation workspace so it never lands in the main chunk (ENG-853).
const ConversationWorkspace = lazy(() => import('./components/conversation/ConversationWorkspace.jsx'))
import { logout as doLogout } from './lib/auth/logout'
import { resolvePrefabForTrigger } from './lib/prefabTrigger'

// The editor surface floats SMALLER than its docked region, so popping it out
// of the dock visibly detaches instead of just growing the editor. One
// constant: the float placement, the frame's fallback size and the float
// button all used to spell it out separately.
const EDITOR_WINDOW_SIZE = { w: 720, h: 560 }
// The OS window it pops out into, when it pops out from DOCKED and so has no
// size of its own to keep. Bigger than the float: a second monitor is why you
// popped out.
const EDITOR_POPOUT_SIZE = { w: 1100, h: 800 }
/**
 * Convorcher NewResponse handler (ENG-834). The trigger is the prefab *name*
 * exactly as convorcher stores it (PascalCase for the legacy seeds, tag-shaped
 * for prefabs authored under the name==tag convention). Resolution is a
 * deterministic normalization against the character's DB-backed prefab rows —
 * replacing the hardcoded 7-entry PREFAB_TRIGGERS map that only covered the
 * legacy Myra seeds and silently no-oped everything else.
 *
 *   trigger resolves to a ready prefab row → EventRuntime.playPrefabConversational
 *     (state-machine + barge-in semantics preserved; see EventRuntime). The
 *     legacy onPrefabTrigger blueprint chain is deliberately NOT fired — it
 *     would double-play the same moment via the old concat path.
 *   no row matches → exactly the pre-ENG-834 behavior: fire onPrefabTrigger
 *     with the raw name so per-character blueprint wiring keeps working.
 *   no trigger at all → onNewResponse event, unchanged.
 *
 * Module-scope (not a component closure) so tests can drive the real handler:
 * exposed as window.__SIMULATE_NEW_RESPONSE__ below.
 */
function handleNewResponse(data) {
  sentenceController.reset()
  const raw = data?.trigger ?? data?.prefab ?? ''
  // Trigger resolution logs are intentionally always-on (not devLog): a
  // response carries at most one trigger, and "why didn't my prefab fire" is
  // the single most-asked debugging question in this flow. Same rationale as
  // the always-on logging in EventRuntime._doPlayPrefab.
  if (raw) {
    const prefab = resolvePrefabForTrigger(raw, CustomAssetManager.listPrefabs())
    if (prefab) {
      console.log(`[prefab] trigger "${raw}" → resolved to prefab "${prefab.tag}" — playing`)
      EventRuntime.playPrefabConversational(prefab)
    } else {
      console.warn(`[prefab] trigger "${raw}" matched NO prefab row (tags: ${CustomAssetManager.listPrefabs().map(p => p.tag).join(', ') || 'none loaded'}) — falling through to the onPrefabTrigger blueprint event`)
      EventRuntime.fireEvent('onPrefabTrigger', { prefab: raw, animName: raw })
    }
  } else {
    devLog('[App] NewResponse → fireEvent(onNewResponse)')
    EventRuntime.fireEvent('onNewResponse', {})
  }
}
// Test hook: lets Playwright drive the real NewResponse path without a live
// Convorcher WS (same pattern as the other window.__* runtime globals).
if (typeof window !== 'undefined') {
  window.__SIMULATE_NEW_RESPONSE__ = handleNewResponse
}


/**
 * Frame the loaded content — the ⌖ button, the F hotkey and Reset Viewport.
 * Returns true when something was framed, false when nothing is loaded.
 */
function frameAvatar() {
  // Whoever loaded the content registered how to frame it — a rig measures its
  // scene graph, a splat bundle its inference branches. There is no generic
  // fallback: with nothing loaded there is nothing to frame.
  return frameContent()
}

if (typeof window !== 'undefined') window.__FRAME_AVATAR__ = frameAvatar

// ─── Sign-out button shown in the bottom bar ──────────────────────────────────
function SignOutButton() {
  const [busy, setBusy] = useState(false)
  const onClick = () => {
    if (busy) return
    setBusy(true)
    // logout() redirects to portal /logout — the browser navigates away,
    // so there's nothing to do after.
    doLogout()
  }
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title="Sign out"
      style={{
        marginRight: 8,
        background: 'transparent', border: '1px solid var(--m-line-hi)',
        color: busy ? 'var(--m-fg-3)' : 'var(--m-fg-3)', borderRadius: 4, padding: '3px 10px',
        fontSize: 11, fontFamily: 'var(--m-font)', cursor: busy ? 'default' : 'pointer',
        flexShrink: 0,
      }}
    >
      {busy ? '…' : 'Sign out'}
    </button>
  )
}


// Drop one key from a plain object without mutating it — used by the
// pendingOps ref/state clear sites below (ENG-1044 Task 11).
function omitKey(obj, key) {
  const { [key]: _drop, ...rest } = obj
  return rest
}

// Count console errors/warnings for the shell's console strip. Module-load
// side effect on purpose: install once, before any component logs.
consoleTally.install()
// Apply the persisted font/icon scale before first paint matters (uiScale.js).
uiScale.apply()

export default function App() {
  // ENG-559 e2e hook: ?boom=1 forces a render-time throw so tests can exercise
  // the top-level ErrorBoundary. Inert (no-op) without the param.
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('boom') === '1') {
    throw new Error('boom: forced render error (ENG-559 e2e hook)')
  }
  // Viewer identity for personal workspaces (ENG-1028): whose characters are
  // "mine", and whether this person may act on someone else's private ones.
  // Presentation only — the server enforces both.
  const { me } = useAuth()
  const [loaded, setLoaded] = useState(false)
  // Surface what AvatarModel is currently doing so a stuck load is visible
  // ("Loading rig…" / "Loading animations 12 / 52" / …). The overlay reads
  // this and falls back to a generic "Loading…" before the first stage fires.
  const [loadingStage, setLoadingStage] = useState(null)
  // The character a switch is resolving TOWARD, before it has committed —
  // `{ id, label } | null`. Exists because commitSwitch resolves the target over
  // the network (clearCharactersCache + loadCharacters + applyCharacterPack)
  // BEFORE it touches `loaded` or `character`, and every loading affordance we
  // had hung off those. So from the click until those awaits resolved, the whole
  // UI still described the OUTGOING character: measured at 150s on a congested
  // box, and a few hundred ms at best, which is still past the 400ms Doherty
  // threshold with nothing acknowledging the click. Set as commitSwitch's first
  // statement and cleared on every exit path, including the failures.
  const [switchingTo, setSwitchingTo] = useState(null)
  // Byte counters for the menubar chip's load readout. Subscribed here rather
  // than inside AppMenuBar because the menubar must stay a pure props-in
  // component — it renders in the popped-out editor window too, where there is
  // no avatar load to report.
  const [loadProgress, setLoadProgress] = useState(null)
  useEffect(() => assetLoadProgress.subscribe(setLoadProgress), [])
  const [error, setError] = useState(null)
  // ENG-991: a splat character whose blueprint has no working renderer chain
  // can NEVER become ready — OGSModel's onReady never fires, and the loading
  // overlay used to spin forever with the reason only in the console. Non-null
  // = the human-readable reason; OGSModel clears it live if the chain appears
  // (an editor fix recovers without a reload). Distinct from `error`: the
  // scene stays mounted and the editor stays usable, so this must not take
  // the terminal error early-return.
  const [renderBlocked, setRenderBlocked] = useState(null)
  // True when the server returned ZERO characters (fresh/empty) — distinct
  // from `error` (a real failure). Drives the create-character empty state.
  const [noCharacters, setNoCharacters] = useState(false)
  // True when the server has characters but the user hasn't explicitly chosen
  // one (no ?character= deep link, no prior in-app selection) — show the
  // launcher instead of guessing list[0]. See src/lib/characterSelection.js.
  const [showLauncher, setShowLauncher] = useState(false)
  // The boot decision — launcher, editor, create-state or error — is not known
  // until the character list resolves. Until then this is the ONLY thing that
  // renders (see the BootSplash branch): `showLauncher === false` used to mean
  // both "show the editor" and "we don't know yet", so every param-less load
  // painted the whole docked shell and then discarded it for the launcher.
  //
  // First boot only. The boot effect clears it and nothing sets it again, so a
  // later character switch keeps the chrome mounted exactly as before.
  const [booting, setBooting] = useState(true)
  // Characters to render in the launcher AND in the menubar's SWITCH TO chip —
  // the single source of truth for "what characters exist". Populated at boot
  // (deep-link or launcher path alike, see the boot effect) and kept fresh by
  // every launcher flow (delete/duplicate/create/sharing). The error-screen
  // fallback effect below also writes here, but only when it has fresh data —
  // see that effect's comment.
  const [launcherChars, setLauncherChars] = useState([])
  // Same list, readable from the popstate listener without resubscribing it on
  // every list change.
  const launcherCharsRef = useRef([])
  // Bumped by every write to launcherChars/launcherCharsRef (see writeCharList
  // below). The [error]-effect captures this at fetch-start and compares at
  // write-time: if it's moved, some OTHER write landed while the fetch was in
  // flight and is guaranteed newer, so the fetch's own result must be dropped —
  // even when that other write was itself an empty list (e.g. the last
  // character just got deleted), which a "ref still empty" proxy can't tell
  // apart from "nobody's written yet". See the effect's comment for the traced
  // race this closes.
  const charListGen = useRef(0)
  // The one place every character-list write goes through, so charListGen can
  // never fall out of sync with launcherChars/launcherCharsRef. Every launcher
  // flow (delete/duplicate/create/sharing), the boot effect, and
  // removeCurrentCharacter all call this instead of setting the three by hand.
  const writeCharList = useCallback((list) => {
    setLauncherChars(list)
    launcherCharsRef.current = list
    charListGen.current++
  }, [])
  // id -> 'deleting' | 'duplicating' (ENG-1044 Task 11). Both the launcher's
  // delete and duplicate calls take seconds on the server (delete takes a full
  // pre-delete checkpoint; duplicate is observed ~13s on staging) with no
  // in-flight signal otherwise — a frozen card, then a change. Set synchronously
  // BEFORE the first await in each handler and cleared on every exit path
  // (success, error, and a refresh that itself fails), via the functional-update
  // form so concurrent ops on other cards are never clobbered.
  const [pendingOps, setPendingOps] = useState({})
  // id -> the name the user chose in the duplicate name prompt (ENG-1044
  // Task 14). Parallel to pendingOps rather than folded into its value
  // shape — 'deleting' entries stay a bare string, and every consumer that
  // only cares about the op kind (re-entry guard, dim/disable styling) never
  // has to unwrap an object it doesn't need. Set together with pendingOps
  // when a duplicate is committed, cleared together on every exit path.
  const [pendingNames, setPendingNames] = useState({})
  // Read inside the handlers' re-entry guard so a second click during the
  // same in-flight op sees the CURRENT map, not the one captured by a stale
  // closure — same reason launcherCharsRef exists alongside launcherChars.
  //
  // Written DIRECTLY at every set/clear site below, never via a useEffect
  // keyed on `pendingOps` — an effect only runs after commit + passive
  // effects, strictly later than the setState that triggered it. Two
  // invocations landing before that effect flushed would both read this ref
  // as empty and both proceed (double DELETE/duplicate) — the guard would
  // exist but not actually guard anything. Writing it inline makes it a
  // synchronous mirror of the map, true the instant `setPendingOps` is
  // called, not on the next render.
  const pendingOpsRef = useRef({})
  // id → 'rig'|'splat'|'stream'|'missing'|'unknown' for the launcher's badges.
  // Resolved by an /ogs probe because the list's `renderer` field is empty on
  // almost every row — see src/lib/characterRigStatus.js.
  const [rigStatusById, setRigStatusById] = useState(null)
  // Character comes from the DB — null until the first resolveActiveCharacter()
  // completes. The canvas and avatar mount only after we have a real character
  // record; if the API is unreachable we show an error overlay instead.
  const [character, setCharacter] = useState(null)

  // Brain & Voice editor (ENG-833) — opened from the menubar's Character menu,
  // edits the active
  // character's Convorcher brain (persona/voice/cues/gestures) over /admin/*.
  const [conversationOpen, setConversationOpen] = useState(false)
  const [conversationTab, setConversationTab] = useState('stories')
  // Appearance / palette picker — opened from the menubar's View menu. Palette selection
  // lives in the paletteStore singleton (CSS vars), not React state.
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  // Keyboard-shortcut help window (Wave 2C) — `?` / Ctrl+/ (handleKeyDown
  // below) or View ▸ "Keyboard Shortcuts". Content is the declarative
  // src/lib/shortcutRegistry.js; the listeners themselves stay distributed.
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false)
  const characterRigRef = useRef(null)
  // Live mirror of the active character id, read by handleError to discard a
  // stale load failure from an AvatarModel/OGSModel instance that has since
  // been superseded by a character switch (see isStaleLoadError). A ref — not
  // the captured `character` closure — because the superseded instance holds an
  // OLD onError closure; the ref is shared across renders so it always reflects
  // the current selection at rejection time.
  const activeCharIdRef = useRef(null)
  // Boot runs exactly once. Guards against React StrictMode's double-invoke of
  // mount effects, which would otherwise fire the boot's side effects twice
  // (e.g. push the stale-character picker dialog twice).
  const didBootRef = useRef(false)

  // Draft state — surfaces unsaved authoring work as a visible badge / banner.
  //   draftState     = 'none' | 'loaded' | 'stale-backend'
  //   dirtyDraft     = the in-memory draft for the active character (or null)
  //   draftIsDirty   = whether the draft has unsaved changes (separate from
  //                    "is there a draft at all" — the draft data stays
  //                    populated post-sync so the Material Editor can keep
  //                    rendering; isDirty flips false on successful sync).
  // The draft store is scoped per-character (keyed by character id) so
  // switching characters never picks up another character's draft. See
  // docs/UX_PRINCIPLES.md — visibility of system status.
  const [draftState, setDraftState] = useState('none')
  const [dirtyDraft, setDirtyDraft] = useState(null)
  const [draftIsDirty, setDraftIsDirty] = useState(false)

  // Reconcile the user's saved palette once (auth has resolved by the time App
  // renders, so scopeKey() and the server call are user-scoped). Applies the
  // per-user local value, then lets the server value win if it differs.
  useEffect(() => { void initPalette() }, [])

  useEffect(() => {
    // Load plugins before the BlueprintEditor mounts so their nodes appear
    // in the palette and saved blueprints with plugin nodes resolve their
    // manifests on first render. Failures throw — surfaced as the App-level
    // error overlay below, per the no-silent-fallbacks rule.
    if (didBootRef.current) return
    didBootRef.current = true
    ;(async () => {
      try {
        const [list] = await Promise.all([
          loadCharacters(),
          loadAllPlugins().catch(e => {
            console.error('[App] Plugin load failed:', e?.message)
            throw new Error(`Plugin load failed: ${e?.message ?? e}`)
          }),
        ])

        // Server is fine but has no characters → explicit create state.
        if (!list || list.length === 0) {
          console.info('[App] No characters on server — showing create-character empty state')
          setNoCharacters(true)
          return
        }

        // Held regardless of which branch we take below, so Back-to-launcher
        // works for a deep-linked boot too (see the popstate effect).
        writeCharList(list)

        // A ?character= deep link is the ONLY thing that skips the launcher.
        // A param-less URL lands on the picker even when a previous selection is
        // stored — opening the app is an explicit choice, not a resume.
        const deepLinked = resolveDeepLinkedCharacterId(list)
        if (!deepLinked) {
          // A param that names a character the server no longer has is a DEAD
          // LINK, and saying so beats quietly showing a grid: the user followed
          // a specific link and needs to know why they didn't land on it. A
          // param-less URL needs no dialog — the launcher IS the answer.
          const requested = readCharacterParam()
          if (requested) {
            console.warn(`[App] ?character=${requested} is not in the server list — showing launcher`)
            await dialog.alert({
              title: 'Character not available',
              body: `“${requested}” is no longer available. Pick a character to continue.`,
              variant: 'error',
            })
          } else {
            console.info('[App] no ?character= deep link — showing launcher')
          }
          setShowLauncher(true)
          return
        }

        // The destination is settled: a character, not the launcher. Drop the
        // splash HERE, before the slow resolve below, so the chrome mounts for
        // the whole of the character load rather than after it.
        //
        // That ordering is the point. `resolveActiveCharacter` (and the rig fetch
        // it leads to) is the long part, and it is exactly when a user needs the
        // menubar — to switch character or clear cache when an asset hangs. The
        // menubar existing through that is the property the whole consolidation
        // was for; only the phase BEFORE we know where we're going has no chrome,
        // because there is nothing there to escape from yet.
        setBooting(false)

        const c = await resolveActiveCharacter()
        if (!c) { setNoCharacters(true); return }
        console.log(`[App] Character loaded: id=${c?.id}, rigPath=${c?.rigPath}`)
        characterRigRef.current = c?.rigPath
        setActiveCharacter(c?.id)
        setDraftState(loadDraftFromLS())
        setDirtyDraft(getCharDraft())
        setDraftIsDirty(hasActiveDraft())
        setCharacter(c)
      } catch (e) {
        console.error('[App] Failed to load character or plugins:', e?.message)
        setError(e?.message ?? 'Failed to load character from DB')
      } finally {
        // Every path above is terminal — launcher, create-state, a loaded
        // character, or an error — so the destination is now known whichever
        // branch ran. In `finally` rather than repeated per branch: an early
        // `return` that forgot this line would leave the splash up forever, and
        // that failure mode is invisible until someone hits that exact path.
        setBooting(false)
      }
    })()
  }, [])

  // Probe each launcher character's renderable geometry once the launcher is up.
  //
  // Deliberately AFTER first paint, not blocking it: the grid is useful
  // immediately and badges fill in a beat later. Only characters with no rigPath
  // cost a request (see probeRigStatuses), so a catalog of ordinary rigged
  // characters is free.
  useEffect(() => {
    if (!showLauncher || !launcherChars.length) return
    let cancelled = false
    ;(async () => {
      const statuses = await probeRigStatuses(launcherChars, listOgsAssets)
      if (!cancelled) setRigStatusById(statuses)
    })()
    return () => { cancelled = true }
  }, [showLauncher, launcherChars])

  // Back / forward across a launcher → character pick.
  //
  // The URL is the navigation state: landing on a URL with no ?character= means
  // the user navigated back PAST their pick, so return to the launcher. This
  // deliberately ignores the stored selection — localStorage is the resume
  // default for a FRESH load, not a navigation fact, and consulting it here
  // would make Back appear to do nothing (the URL would change while the view
  // stayed put, which is exactly the kind of silent behaviour this repo forbids).
  useEffect(() => {
    const onPop = () => {
      if (!launcherCharsRef.current.length) return
      const hasParam = new URLSearchParams(window.location.search).has('character')
      // Symmetric: no param → the launcher, a param → the character. Both
      // directions matter, or Back out of the launcher changes the URL while
      // leaving the picker on screen.
      setShowLauncher(!hasParam)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Live-preview support: when the authoring draft picks a new rig (blob URL),
  // update character state so AvatarModel remounts with the new file.
  useEffect(() => {
    const handler = draft => {
      setDirtyDraft(draft)  // keep the dirty pill in sync with the in-memory draft
      setDraftIsDirty(hasActiveDraft())  // pill visibility tracks dirty flag, not draft presence
      if (!draft?.rigPath) return
      if (draft.id !== character?.id) return
      if (draft.rigPath === characterRigRef.current) return
      devLog('[App] Draft rig change:', draft.rigPath)
      characterRigRef.current = draft.rigPath
      // Only mirror rigPath into character state — never spread the whole
      // draft. Shallow `{...c, ...draft}` makes character.materials a
      // reference to draft.materials, so all subsequent draft edits silently
      // mutate character too — that broke the unsaved-changes diff (every
      // key always matched because it was the same object). Material live
      // preview is driven separately via window.__RELOAD_MATERIALS__.
      setCharacter(c => ({ ...c, rigPath: draft.rigPath }))
    }
    subscribeDraft(handler)
    return () => unsubscribeDraft(handler)
  }, [character?.id])

  // Keep the live active-character id in sync so handleError can discard a
  // late load failure from a character we've already switched away from.
  useEffect(() => {
    activeCharIdRef.current = character?.id ?? null
    // The previous character's framing does not describe this one; whoever
    // loads the new content publishes a fresh look-at.
    clearContent()
  }, [character?.id])

  const [avatarInfo, setAvatarInfo] = useState({
    bodyMeshLoaded: false,
    faceMeshLoaded: false,
    skeletonBones: 0,
    morphTargetCount: 0,
  })

  // Restore UI state across forced reloads (e.g. forceToon, character switch).
  // LiveTunePanel writes `aos-reload-state` to sessionStorage before reload;
  // we read it once here and clear it so it doesn't persist beyond one reload.
  const reloadRestore = useMemo(() => {
    if (typeof window === 'undefined') return null
    try {
      const raw = sessionStorage.getItem('aos-reload-state')
      if (raw) { sessionStorage.removeItem('aos-reload-state'); return JSON.parse(raw) }
    } catch { /* ignore */ }
    return null
  }, [])

  // ── Pop-out: the editor surface portals into a real OS window ──────────────
  // FloatingWindow 'popped' mode (useWindowPopOut) — the SAME BlueprintEditor
  // instance keeps rendering, only its DOM lands in the popup document. Same
  // module graph state, same active tab; the old second-boot popup
  // (?editor_popup=1) always opened on the body graph with divergent state.
  const {
    popupBody: editorPopupBody,
    popOut: popOutEditorWindow,
    popIn: popInEditor,
  } = useWindowPopOut('editor-surface', { title: 'BLUEPRINT EDITOR', defaultSize: EDITOR_POPOUT_SIZE })

  // This window owns the avatar (AvatarModel installs __RELOAD_MATERIALS__).
  // Honor reload requests broadcast by ANOTHER TAB running the app on the same
  // character, so a material edit made there refreshes the character here. The
  // material graph's own pop-out no longer needs this — it is a portal of this
  // instance now, not a second boot — but a second tab still does.
  useEffect(() => subscribeMaterialReload(), [])

  const handlePopOut = useCallback(() => {
    // The pop-out shows whatever surface is open. From the View menu with no
    // surface open there is nothing to portal yet — open the body blueprint
    // (the historical pop-out default) so the OS window never opens empty.
    if (!menubarCommands.get().state.activeBP) menubarCommands.run('editors.open', 'body')
    popOutEditorWindow()
  }, [popOutEditorWindow])

  // Convorcher connection state
  const [wsState, setWsState] = useState('disconnected') // disconnected | connecting | connected
  const [frameStats, setFrameStats] = useState({ buffered: 0, played: 0, totalReceived: 0, sentenceNum: -1 })
  // Stable buffer instance — lives for the app lifetime
  const animationBuffer = useMemo(() => new AudioAnimationBuffer(), [])

  // Wire buffer to SentenceController after React settles on the kept useMemo instance
  useEffect(() => {
    sentenceController.setBuffer(animationBuffer)
    // Expose the live A2F buffer for tests + console inspection (debug global).
    if (typeof window !== 'undefined') window.__AUDIO_ANIMATION_BUFFER__ = animationBuffer
  }, [animationBuffer])

  // Stable client instance
  // Wire SentenceController callbacks → EventRuntime
  useMemo(() => {
    sentenceController.onGesture             = (d) => EventRuntime.fireEvent('onGesture',             d)
    sentenceController.onSentenceStarted     = (d) => EventRuntime.fireEvent('onSentenceStarted',     d)
    sentenceController.onSentenceCompleted   = (d) => EventRuntime.fireEvent('onSentenceCompleted',   d)
    sentenceController.onLastSentenceStarted = (d) => EventRuntime.fireEvent('onLastSentenceStarted', d)
    sentenceController.onAllSentencesPlayed  = ()  => EventRuntime.fireEvent('onAllSentencesPlayed',  {})
    sentenceController.onSentenceWaiting     = ()  => EventRuntime.fireEvent('onSentenceWaiting',    {})
    sentenceController.onSentenceDataReady   = (d) => EventRuntime.fireEvent('onSentenceDataReady',  d)
    sentenceController.onFirstSentenceReady  = (d) => EventRuntime.fireEvent('onFirstSentenceReady', d)
  }, [])

  const client = useMemo(() => {
    const c = new ConvorcherClient({
      onConnect: () => {
        setWsState('connected')
        EventRuntime.fireEvent('onWSConnected', {})
      },
      onDisconnect: (code, reason) => {
        setWsState('disconnected')
        // `message` is the display string; `code`/`reason` stay for the graph's
        // data pins and the console. A print node wired to this event used to
        // put the raw payload on screen — `{"code":1006,"reason":""}` — which
        // is the least actionable text available, since 1006 means "no close
        // frame" and therefore never carries a reason.
        EventRuntime.fireEvent('onWSDisconnected', {
          code,
          reason,
          message: describeWebSocketClose(code, reason),
        })
      },
      onError: () => setWsState('disconnected'),
      onServerError: (d) => EventRuntime.fireEvent('onServerError', d),
      onSession: (id, data) => {
        devLog('[App] Session:', id, 'features:', data?.features)
        setConstant('session_id', id)
        // Expose each server feature flag as a blueprint-readable constant
        // named features_<key> (e.g. features_ttsaf). Blueprints can read
        // these via getVariable() which falls back to constants.
        for (const [k, v] of Object.entries(data?.features ?? {})) {
          setConstant(`features_${k}`, v)
        }
        EventRuntime.fireEvent('onSession', {
          sessionId: id,
          features: JSON.stringify(data?.features ?? {}),
        })
      },
      onTextOut: (msg) => {
        devLog('[App] TextOut sentenceNum=%d isLast=%s text="%s"', msg.sentenceNum, msg.isLast, msg.text?.slice(0, 60))
        sentenceController.addSentenceMeta(msg.sentenceNum, {
          text:       msg.text,
          isLast:     msg.isLast,
          // The FULL per-sentence animation list — convorcher can attach
          // several (a compound reply like "walks while crouching then jumps"
          // arrives as one animation per action). SentenceController fires one
          // onGesture per item in order; gestureSequencer plays the generated
          // clips back-to-back. Per item:
          //   tracks       — raw kimodo payload when convorcher served a
          //                  generated gesture from its pool; absent for a
          //                  stock gestures.json tag.
          //   motion_class — 'performance' | 'gesture' | undefined, QC's
          //                  classification for a generated clip: 'performance'
          //                  routes to the override channel, 'gesture'/absent to
          //                  the additive selection channel. Absent means
          //                  'gesture' (old servers, non-generated tags).
          animations: (msg.animations ?? []).map(a => ({
            animName:    a?.Name       ?? '',
            actionText:  a?.ActionText ?? '',
            tracks:      a?.tracks,
            motionClass: a?.motion_class,
          })),
        })
      },
      onNewResponse: handleNewResponse,
      onStreamChunk: (msg) => {
        if (msg.done) devLog('[App] StreamChunk DONE sentenceNum=%d frames=%d', msg.sentenceNum, msg.frames?.length ?? 0)
        sentenceController.addStreamChunk(msg)
      },
      onAudioAnimation: (msg) => {
        devLog('[App] AudioAnimation sentenceNum=%d frames=%d audio=%d bytes', msg.sentenceNum ?? 0, msg.frames?.length ?? 0, msg.audio?.length ?? 0)
        sentenceController.addFrames(msg.sentenceNum ?? 0, msg.frames, msg.audio)
      },
      onResponseComplete: (msg) => {
        devLog('[App] ResponseComplete sentenceCount=%d → fireEvent(onResponseComplete)', msg?.data?.sentenceCount ?? msg?.sentenceCount)
        EventRuntime.fireEvent('onResponseComplete', {
          sentenceCount: msg?.data?.sentenceCount ?? msg?.sentenceCount,
        })
      },
      onMetrics: (data) => {
        devLog('[App] Metrics received:', data)
        EventRuntime.fireEvent('onMetrics', data ?? {})
      },
    })
    return c
  }, [])

  // Wire EventRuntime action fns → client + auto-connect.
  //
  // Defer the first connect by a tick so React StrictMode's mount/unmount/
  // remount pattern in dev can cancel it before we ever open a socket —
  // otherwise the first WS opens, is torn down mid-handshake, and the
  // browser logs "WebSocket is closed before the connection is established"
  // into the console.
  useEffect(() => {
    EventRuntime.setSendTextInput((text) => {
      devLog('[App] sendTextInput → TextIn "%s" connected=%s', text, client.connected)
      client.sendTextIn(text)
    })
    // The `convorcherSession` node declares the session (character + features)
    // from the graph; route it to the WS client. When such a node is present
    // the character-push effect below defers to it.
    EventRuntime.setApplyConvorcherSession((cfg) => client.setSessionConfig(cfg))
    // ENG-1062: route a state-machine transition's Actions (custom-event names)
    // to fireCustomEvent. Decoupled seam so GraphEvaluator never imports the
    // event runtime onto the animation hot path.
    setTransitionActionHandler((name, data) => EventRuntime.fireCustomEvent(name, data))
    // Test hooks for the interop E2E suite (ENG-705). Live singletons, not React state.
    if (typeof window !== 'undefined') {
      window.__CONVORCHER_CLIENT__ = client
      window.__SENTENCE_CONTROLLER__ = sentenceController
      // Test seam for the onSession-derived constants (features_gesture_gen, …),
      // mirroring __SET_VARIABLE__ (AvatarModel.jsx). Lets a CI spec drive the
      // gesture-gen gate without a live onSession payload. Additive; nothing renamed.
      window.__SET_CONSTANT__ = setConstant
    }
    // The first connect is NOT started here — it waits for the session
    // identity to be known. See the session-config effect further down.
    return () => {
      EventRuntime.setSendTextInput(null)
      EventRuntime.setApplyConvorcherSession(null)
      setTransitionActionHandler(null)
      client.stopAutoConnect()
      if (window.__CONVORCHER_CLIENT__ === client) delete window.__CONVORCHER_CLIENT__
      if (window.__SENTENCE_CONTROLLER__ === sentenceController) delete window.__SENTENCE_CONTROLLER__
    }
  }, [client])

  // E2E test hook: expose the loaded event graph node types so tests can verify
  // the correct blueprint was loaded without inspecting localStorage. This is the
  // authoritative source for verifying blueprint load success (ENG-539 Stage 2).
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__EVENT_GRAPH_NODES__ = () => eventGraphState.nodes.map(n => n.type)
    return () => {
      if (window.__EVENT_GRAPH_NODES__) delete window.__EVENT_GRAPH_NODES__
    }
  }, [])

  // Poll frame stats for debug HUD
  useEffect(() => {
    const interval = setInterval(() => {
      setFrameStats({ ...animationBuffer.stats })
    }, 200)
    return () => clearInterval(interval)
  }, [animationBuffer])

  const handleKeyDown = useCallback((e) => {
    if (e.target.matches('input, textarea, select') || e.target.isContentEditable) return
    // ? or Ctrl/⌘+/ — toggle the keyboard-shortcut help window (Wave 2C).
    // Checked before fireInputKey so the help chord never leaks into the
    // blueprint Input Key nodes.
    if (e.key === '?' || ((e.ctrlKey || e.metaKey) && e.key === '/')) {
      e.preventDefault()
      setShortcutHelpOpen(v => !v)
      return
    }
    EventRuntime.fireInputKey(e.key)
    // F — frame the avatar to the viewport based on its world-space bbox.
    if (e.key === 'f' || e.key === 'F') frameAvatar()
  }, [])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // ── Print messages from Event Graph ──
  const [printMessages, setPrintMessages] = useState([])
  useEffect(() => {
    const id = setInterval(() => {
      EventRuntime.prune()
      setPrintMessages([...EventRuntime.messages])
    }, 100)
    return () => clearInterval(id)
  }, [])

  // ?live=1 — legacy standalone-viewer flag, kept so deployed links don't
  // break (Wave 2B: the Standard/Live mode fork is gone — the app is ALWAYS
  // live/playing). Its one remaining effect is the App-level blueprint
  // hydration below (AAM snapshot / ?blueprint= without waiting on the
  // editor), which is what those links relied on. Splat environment URL can
  // be overridden via ?splat=<url>.
  const { isLiveFromUrl, splatUrl, explicitBlueprintUrl, environmentId } = useMemo(() => {
    if (typeof window === 'undefined') return { isLiveFromUrl: false, splatUrl: undefined, explicitBlueprintUrl: null, environmentId: null }
    const params = new URLSearchParams(window.location.search)
    return {
      isLiveFromUrl: params.get('live') === '1',
      splatUrl: params.get('splat') ?? undefined,
      explicitBlueprintUrl: params.get('blueprint'),  // null means "respect localStorage"
      environmentId: params.get('environment'),       // deep-link a character INTO an env
    }
  }, [])

  // Splat mode: the active character renders through the TexAvatars splat
  // pipeline (OGSModel) instead of the rig path (AvatarModel). Driven purely
  // by the resolved `character` (isSplatCharacter — e.g. the `isaac-gs`
  // character); the legacy ?isaac=1 URL gate has been retired. `character` is
  // null until resolveActiveCharacter() completes, so this is false during
  // initial load — correct, since neither avatar path mounts before a
  // character exists.
  // `?ogscontent=1` forces the splat pipeline for a character whose OGS bundle
  // lives as loose /content/ogs uploads (dev path — see ogsContentPacks.js).
  //
  // Both terms are pure reads of the character being rendered (or the URL) —
  // render mode must never be cached in module-level state, which cannot say
  // which character it describes and so survives a switch that invalidates it.
  // Stream mode: the active character renders through the server-side OGS-stream
  // (StreamAvatarModel) instead of the rig / on-device-splat paths. Derived from the
  // character (`renderer === 'stream'`) OR asked for per-session with ?stream=1 — the
  // same /ogs bundle the browser lifts on-device is the one the server streams, so
  // streaming is a way to RENDER a character rather than a kind of character.
  // `streamChar` is the id forwarded to the stream server (?character=).
  const streamChar = isStreamCharacter(character) ? character.id : null

  // Resolved BEFORE splatMode and subtracted from it: a splat character asked to
  // stream is a splat character, so both would otherwise be true and BOTH renderers
  // would mount on the same subject — two avatars, one scene.
  const splatMode = !streamChar
    && (isSplatCharacter(character) || (!!character && isOgsContentForced()))

  // Rig is optional — no rig GLB means "show the loud rig-missing overlay" (CheckMissingRig), not an error/freeze.
  const rigMissing = !!character && !splatMode && !streamChar && !checkForRig(character)

  // A splat character with no authored blueprint auto-loads the STARTER for its
  // kind, so the driving nodes are always present. The template is named after
  // the kind of character it serves, never after a character (ENG-1022): this
  // used to be `blueprints/isaac.json`, which had no renderer node — so the
  // phase settled at 'three' and the pipeline never started — and whose deletion
  // would have errored every gaussian character at once. An explicit ?blueprint=
  // still overrides it.
  const effectiveBlueprintUrl = explicitBlueprintUrl
    ?? (splatMode ? starterTemplateUrl(resolveStarterKind({ splat: true })) : null)

  // The starter template is shared by every gaussian character, so its branch
  // nodes are generated per bundle: region count is a property of the BUNDLE
  // (head/body/clothes here, head/body elsewhere), which is exactly what a fixed
  // file cannot express — and is how a 3-region character ended up rendering
  // nothing. Only for the starter: an explicit ?blueprint= is loaded verbatim.
  const usingStarter = splatMode && !explicitBlueprintUrl
  const buildStarterTransform = useCallback(async (charId) => {
    if (!usingStarter) return undefined
    const rows = await listOgsAssets(charId).catch(() => null)
    const { regions, error } = await fetchStarterRegions({
      rows,
      fetchJson: async (url) => (await fetchAamFile(url)).json(),
    })
    if (error) {
      console.error(`[starter] ${charId}: could not read the bundle's regions (${error}) — falling back to the head region alone. Other regions will not render.`)
    }
    return (data) => {
      const { data: out, skipped } = applyStarterRegions(data, regions)
      for (const s of skipped) {
        console.error(`[starter] ${charId}: bundle region "${s.region}" has no renderer pins (${s.pins.join(', ')}) — that region will not render. The OGS renderer plugin needs the pair declared.`)
      }
      console.log(`[starter] ${charId}: gaussian starter, regions [${regions.join(', ')}]`)
      return out
    }
  }, [usingStarter])

  // Wave 2B "one mode": the liveView state (and its ▶/■ transport + View ▸
  // Live Mode toggle) is gone — the app always behaves as live/playing while
  // staying editable. window.__SET_LIVE_VIEW__ survives as a no-op because
  // CLAUDE.md forbids removing window.__* debug globals (and existing
  // callers/tests still poke it); there is no mode left for it to flip.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__SET_LIVE_VIEW__ = () => {}
    return () => { if (window.__SET_LIVE_VIEW__) delete window.__SET_LIVE_VIEW__ }
  }, [])
  const [tuneVisible, setTuneVisible] = useState(() =>
    typeof window !== 'undefined' && (
      new URLSearchParams(window.location.search).get('tune') === '1' ||
      localStorage.getItem('tune') === '1'
    )
  )
  // Horizontal space the docked Outliner rail occupies, so left-edge overlays
  // clear it instead of rendering underneath (0 when it's hidden).
  const [animVisible, setAnimVisible] = useState(() =>
    typeof window !== 'undefined' && (
      new URLSearchParams(window.location.search).get('anim') === '1' ||
      localStorage.getItem('anim') === '1'
    )
  )
  // Rig Preview face-control board (ENG-946). Same gate shape as the anim
  // panel; also openable from the Rig Controller node's button via
  // window.__RIG_PREVIEW__.
  const [rigVisible, setRigVisible] = useState(() =>
    typeof window !== 'undefined' && (
      new URLSearchParams(window.location.search).get('rig') === '1' ||
      localStorage.getItem('rig') === '1'
    )
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__RIG_PREVIEW__ = {
      open:  () => setRigVisible(true),
      close: () => setRigVisible(false),
    }
    return () => { if (window.__RIG_PREVIEW__) delete window.__RIG_PREVIEW__ }
  }, [])

  // Splat mode needs the shipped isaac blueprint hydrated (into graph refs +
  // localStorage) before render, in BOTH editor and live mode, so the editor
  // shows the Facepalm -> Isaac Splat chain and the face runtime ticks those
  // nodes.
  //
  // Tracked as "what did we actually hydrate, for whom" rather than a ready
  // boolean (ENG-907). `splatMode` for an OGS character only becomes true once
  // the /ogs pack fetch lands, mid-flight — and in the very commit where it
  // flips, a separate `ready` boolean is still stale-true (the effect that
  // clears it hasn't run, and effects in that commit close over the old value).
  // Anything gated on it — like "is it safe to open the convorcher session
  // yet?" — got a one-commit window where it read ready while the graph was
  // still empty. Comparing what we hydrated against what's required has no such
  // window: the moment the requirement appears, the two differ.
  //
  // The key carries the character, not just the url, because the graph refs are
  // global: switching splat → rig loads the rig character's blueprint over them,
  // so coming back needs a re-hydrate even though the url is unchanged.
  const [hydratedBlueprint, setHydratedBlueprint] = useState(null)
  const needsShippedBlueprint = splatMode && !!effectiveBlueprintUrl
  const requiredBlueprint = needsShippedBlueprint ? `${character?.id ?? ''}::${effectiveBlueprintUrl}` : null
  const shippedBlueprintReady = !needsShippedBlueprint || hydratedBlueprint === requiredBlueprint

  // In live mode, hydrate graph state before firing beginPlay. The
  // BlueprintEditor (which normally does this) isn't mounted in live mode.
  // The AAM cloud snapshot is the SOLE authority (ENG-798): the viewer never
  // reads localStorage, so a stale local cache can no longer shadow the
  // correct server blueprint (which is what forced "Load from Cloud every
  // time"). An explicit ?blueprint= URL still overrides for reset/testing.
  const [blueprintReady, setBlueprintReady] = useState(!isLiveFromUrl)
  useEffect(() => {
    if (!isLiveFromUrl) return
    // Wait for the character to resolve before choosing a blueprint source.
    if (!character) return
    let cancelled = false
    ;(async () => {
      try {
        const charId = character.id ||
          (typeof window !== 'undefined' && window.location
            ? new URLSearchParams(window.location.search).get('character') ?? 'myra'
            : 'myra')

        if (effectiveBlueprintUrl) {
          // Explicit ?blueprint= URL override (reset / load a shipped file).
          console.log(`[App] blueprintReady effect: loading ?blueprint=${effectiveBlueprintUrl}`)
          await loadBlueprint(effectiveBlueprintUrl, {
            characterId: charId,
            transform: await buildStarterTransform(charId),
          })
        } else {
          // Cloud is the only authority. Read the AAM snapshot; no localStorage
          // read/write. Fail LOUD if it's missing — no silent fallback that
          // could serve a stale or default graph.
          console.log(`[App] blueprintReady effect: reading AAM snapshot for ${charId}`)
          const result = await loadBlueprintFromDB(charId)
          // Superseded means another character started loading — that load owns
          // the graphs now, so this one just stands down. Only a genuine miss is
          // an error.
          if (result?.superseded) return
          if (!result) {
            throw new Error(`Blueprint not found on server for character ${charId}`)
          }
        }

        if (!cancelled) setBlueprintReady(true)
      } catch (err) {
        console.error('[App] live blueprint load failed:', err)
        if (!cancelled) setError(err?.message || String(err))
      }
    })()
    return () => { cancelled = true }
  }, [isLiveFromUrl, effectiveBlueprintUrl, character?.id])

  // ?environment=<id> deep-link: activate one of the character's baked
  // environments on load, the same way the content-browser / inspector do
  // (applyEnvironment sets liveTuneStore.environment.splatUrl + flips into
  // Live View). Without this the env is merely in the list but never becomes
  // the active splat, so the runtime lighting (LightProbeVolume / reflection
  // captures) never engages. Resolves the asset by id from the org bucket;
  // a missing id fails LOUD (no silent fallback) but does not crash the app.
  // Runs once the environment id is set.
  useEffect(() => {
    if (!isOrgEnvironmentsEnabled() || !environmentId) return
    let cancelled = false
    ;(async () => {
      try {
        const list = await listOrgEnvironments()
        if (cancelled) return
        const asset = (Array.isArray(list) ? list : list?.environments ?? [])
          .find(e => e.id === environmentId)
        if (!asset) {
          console.error(`[App] ?environment=${environmentId} not found in org bucket — not activating.`)
          if (typeof window !== 'undefined') window.__ENV_DEEPLINK_MISS__ = true
          return
        }
        // source 'url': show it now, but never write it into this browser's
        // stored tune. A shared link is an instruction for THIS load; persisting
        // it made every later visit open in the sender's environment, and a
        // stored snapshot then outranks the next URL (the same way it silently
        // disabled ?probe=1 — see liveTuneStore's assetId note).
        applyEnvironment(asset, null, { source: 'url' })
      } catch (err) {
        console.error(`[App] failed to load ?environment=${environmentId}:`, err?.message || err)
      }
    })()
    return () => { cancelled = true }
  }, [environmentId])

  // Splat mode — preload the starter blueprint into the graph refs +
  // localStorage even in EDITOR mode (the live-mode effect above only runs
  // under ?live=1). The editor reads localStorage on mount and the face
  // runtime reads the graph refs, so this single fetch makes both the editor
  // view and the rendered splats show the same graph.
  // Reactive to splatMode so it fires when a splat character is selected
  // in-place from the Character menu. Idempotent with the live effect
  // (loadBlueprint just rewrites the same keys).
  useEffect(() => {
    if (!needsShippedBlueprint) return
    if (hydratedBlueprint === requiredBlueprint) return
    let cancelled = false
    ;(async () => {
      try {
        const charId = character?.id ?? null
        const transform = await buildStarterTransform(charId)
        if (cancelled) return
        await loadBlueprint(effectiveBlueprintUrl, { characterId: charId, transform })
        if (!cancelled) setHydratedBlueprint(requiredBlueprint)
      } catch (err) {
        console.error('[starter] blueprint preload failed:', err)
        if (!cancelled) setError(err?.message || String(err))
      }
    })()
    return () => { cancelled = true }
  }, [needsShippedBlueprint, effectiveBlueprintUrl, requiredBlueprint, hydratedBlueprint, buildStarterTransform, character?.id])

  // Open the onConstruct gate once the graphs are hydrated AND the avatar has
  // finished loading (Wave 2B: beginPlay merged into onConstruct). Opening
  // fires the once-per-load construct — the old beginPlay fire — and from
  // then on every graph notification (hydration, editor edit) re-fires it.
  // Opening earlier would run voxyConfig / convorcherSession / camera-rig
  // setup before the runtime hooks they depend on are in place; a character
  // switch drops `loaded` and closes the gate again so the next graph waits
  // for the next avatar.
  useEffect(() => {
    if (!blueprintReady || !loaded) {
      EventRuntime.setConstructReady(false)
      return
    }
    // Deferred a tick, same as the old beginPlay fire: the avatar-loaded
    // render commit finishes before the chain runs.
    const t = setTimeout(() => EventRuntime.setConstructReady(true), 0)
    return () => clearTimeout(t)
  }, [blueprintReady, loaded])

  // ── The convorcher session: ONE per load, opened as the right character ──
  //
  // The rendered character and the conversational one differ for splat/OGS
  // avatars: `isaac-ogs` renders from rig params and has no persona of
  // its own, so its Event Graph declares `character: "isaac"` on a
  // `convorcherSession` node. That node only fires on beginPlay, which is gated
  // on the avatar having finished LOADING — so pushing the rendered slug at
  // mount (as this used to) opened a session against a slug with no
  // `brain_characters` row. Convorcher answers with text but drops TTS ("no
  // voice ID configured"), so the reply carries zero audio and zero ARKit
  // frames, and the loading overlay does not protect the window: it blocks the
  // pointer but not Tab focus, so three Tab presses reach the message input.
  // Prod logs measured the window at 1s–12min (ENG-907).
  //
  // So: wait until the graph that would declare the session has had its chance
  // to hydrate, resolve ONCE through sessionCharacter.resolveSessionConfig, and
  // only then open the socket. The node's own apply on beginPlay then finds the
  // same config already in place and no-ops (setSessionConfig early-returns when
  // nothing changed) instead of discarding a live session.
  //
  // Gate parts, all required:
  //   character?.id        — before it resolves we can't tell a splat character
  //                          from a rig one (and a session with Character:""
  //                          is what produced the `no A2F endpoint configured
  //                          for character ""` lines in the same logs).
  //   blueprintReady       — live mode's AAM/?blueprint= hydration.
  //   shippedBlueprintReady — splat mode's shipped-blueprint hydration.
  //
  // Not covered: plain editor mode on a NON-splat character, where
  // BlueprintEditor owns the cloud hydration and neither flag tracks it. Such a
  // character's graph would have to declare a persona different from its own
  // slug to be affected; none does today. Same for switching characters
  // in-place without a reload — the graph can lag the new character there, which
  // is pre-existing behaviour this change deliberately leaves alone.
  const sessionIdentityReady = !!character?.id && blueprintReady && shippedBlueprintReady
  useEffect(() => {
    if (!sessionIdentityReady) return
    const cfg = resolveSessionConfig({ renderedSlug: character.id, nodes: eventGraphState.nodes })
    if (!client.isAutoConnecting()) {
      // First session of this load. seedSessionConfig (not setSessionConfig)
      // because there is nothing to invalidate yet — clearing the persisted
      // session id here would break refresh-resume for exactly these characters.
      client.seedSessionConfig(cfg)
      console.log(`[Convorcher] session character: ${cfg.character} (from ${cfg.source})`)
      // Deferred a tick so React StrictMode's mount/unmount/remount in dev can
      // cancel it before a socket is ever opened — otherwise the first WS is
      // torn down mid-handshake and the browser logs "WebSocket is closed
      // before the connection is established".
      const t = setTimeout(() => client.startAutoConnect(), 0)
      return () => clearTimeout(t)
    }
    // Already live — a character switch. This drops the old session and
    // reconnects, and no-ops when the resolved config is unchanged.
    client.setSessionConfig(cfg)
  }, [client, character?.id, sessionIdentityReady])

  // One-shot: warm AudioContext on the very first pointerdown anywhere.
  // Covers users who click the canvas or Anim Test Panel before focusing
  // the text input. Keyboard-only users are covered by the input's onFocus.
  useEffect(() => {
    const warm = () => {
      try { audioPlayer.warmup() } catch { /* ignore */ }
    }
    window.addEventListener('pointerdown', warm, { once: true })
    return () => window.removeEventListener('pointerdown', warm)
  }, [])

  // Swallow file drops that miss a designated drop zone. Without this, dropping
  // a file (e.g. a .glb rig onto the create-character wizard but slightly off the
  // zone) triggers the browser default — it navigates to / opens the dropped
  // file, blanking the whole SPA. Reported as "drag-drop froze everything".
  // Real drop zones keep their own onDrop handlers (they run first); this only
  // catches strays that would otherwise hijack the page.
  useEffect(() => {
    const prevent = (e) => e.preventDefault()
    window.addEventListener('dragover', prevent)
    window.addEventListener('drop', prevent)
    return () => {
      window.removeEventListener('dragover', prevent)
      window.removeEventListener('drop', prevent)
    }
  }, [])

  // ── Voxy voice input ──
  //
  // Voxy is no longer auto-booted. The event graph owns the lifecycle via
  // the `voxyConfig` node: wire it from beginPlay (or any other event) and
  // its runtime calls voxyManager.init() + start(). App only tracks the
  // active flag and the last error; the mic control itself lives in
  // <ChatInputBar>, which rebuilds it on a full-tree swap (e.g. a switch to a
  // splat character) — the regression voxy-widget-switch.spec.js guards.
  const [voxyActive, setVoxyActive] = useState(() => voxyManager.isActive())
  const [voxyError, setVoxyError] = useState(() => voxyManager.getLastError())

  useEffect(() => {
    const unsub = voxyManager.onActiveChange((active) => {
      setVoxyActive(active)
      setVoxyError(voxyManager.getLastError())
    })
    return () => {
      unsub()
      voxyManager.stop()
    }
  }, [])

  // Loud-failure state for audio OUTPUT: the playback AudioContext is created
  // 'suspended' when the page has no user activation yet (mobile autoplay
  // policy — a pure-voice session may never tap the page). A suspended
  // context never fires source.onended, which is what drives
  // onSentenceCompleted, so TTS is silent AND the conversation loop wedges.
  // AudioPlayer resumes itself from the next tap/keypress; this pill makes
  // the "tap needed" state visible instead of silently swallowing responses.
  const [audioCtxState, setAudioCtxState] = useState(() => audioPlayer.getState())
  useEffect(() => {
    const unsub = audioPlayer.onStateChange(setAudioCtxState)
    return unsub
  }, [])
  const audioBlocked = audioCtxState !== null && audioCtxState !== 'running'

  // Auto-stop voxy when the voxyConfig node is removed from the graph.
  // The node owns voxy's lifecycle, so deleting it should mirror that in
  // runtime state — widget unmounts, mic releases, VoxyCore tears down.
  useEffect(() => {
    const check = () => {
      if (!voxyManager.isActive()) return
      // One level into graphGroup containers too — GLOBAL_UNIQUE_TYPES (EventGraph.jsx)
      // permits a voxyConfig to live inside a group, and this check must see it there
      // or a grouped voxyConfig gets torn down by the very effect meant to mirror its
      // deletion (see EventRuntime.hasNodeOfType doc comment).
      const hasConfig = EventRuntime.hasNodeOfType('voxyConfig')
      if (!hasConfig) voxyManager.stop()
    }
    const unsub = subscribeEventGraph(check)
    check()  // run once in case the node is already absent at mount time
    return unsub
  }, [])

  // Auto-teardown headTrackedCamera when its node is deleted OR UNWIRED from
  // the graph (ENG-981 rework — mirrors the voxyConfig watcher above, but
  // checks REACHABILITY rather than mere presence). The node is an ordinary
  // exec node: arming/config only ever happens when its exec chain actually
  // runs (EventRuntime.js's `headTrackedCamera` case). A presence-only check
  // (EventRuntime.hasNodeOfType) missed the case that matters most here:
  // deleting the WIRE (not the node) leaves the node present but reachable
  // from nothing, so no exec chain ever re-syncs it AND the old presence
  // check stayed quiet — an armed/tracking webcam session with no path left
  // to disarm it. isNodeTypeReachable (src/lib/execReachability.js) asks the
  // structural question directly: is the node the target of some exec chain
  // from ANY event root (onConstruct, beginPlay, or anything else) — not
  // "did THIS pass's fired event reach it", since reachability is a property
  // of the graph, not of which event happened to fire. That also covers a
  // character/blueprint switch for free: loadBlueprint.js's post-hydration
  // notify runs this same check, so a new graph whose headTrackedCamera node
  // (if any) is unreachable tears down the OLD graph's still-armed runtime
  // singleton, which would otherwise survive the in-SPA switch untouched.
  useEffect(() => {
    const check = () => {
      if (headTrackedCamera.state === 'idle') return
      const { nodes, edges } = eventGraphState
      if (!isNodeTypeReachable('headTrackedCamera', nodes, edges)) {
        headTrackedCamera.syncFromGraph(null)
      }
    }
    const unsub = subscribeEventGraph(check)
    check()  // run once in case the node is already absent/unwired at mount time
    return unsub
  }, [])

  // Unload a blueprint-authored environment when no `loadEnvironment` node is
  // reachable any more (ENG-1031). Same shape as the headTrackedCamera watcher
  // above and for the same reason: deleting a node — or cutting its wire — must
  // undo what the node did, and neither goes through an exec chain, so only a
  // reachability check notices. Without this, deleting the node left the splat
  // on screen with nothing in the graph asking for it.
  //
  // The type is REPEATABLE (one on onConstruct, another on a key press is a
  // normal graph), so this asks isAnyNodeOfTypeReachable — the unique-type
  // helper stops at the first node it finds and would tear down on an unwired
  // one while a live one still drove the feature.
  //
  // unloadAuthoredEnvironment refuses to touch a HUMAN pick, so editing the
  // graph can never throw away an environment chosen in the content browser.
  useEffect(() => {
    const check = () => {
      const { nodes, edges } = eventGraphState
      if (isAnyNodeOfTypeReachable('loadEnvironment', nodes, edges)) return
      if (unloadAuthoredEnvironment()) {
        console.info('[App] unloaded the blueprint-authored environment — no loadEnvironment node is wired any more')
      }
    }
    const unsub = subscribeEventGraph(check)
    check()
    return unsub
  }, [])

  // Same rule for the backdrop a `setBackdrop` node authored (the Scene Look
  // group Settings ▸ Scene writes). Without this, a look set on one character
  // outlived it: switching from a character WITH a saved background to one
  // WITHOUT fires no setBackdrop at all, so nothing revoked the old value and
  // the new character wore the previous one's colour — reported on ANI → Myra,
  // and the whole point of the feature is that a look is PER CHARACTER.
  //
  // revertAuthored only touches keys marked 'blueprint', so a colour the user
  // picked in Settings ▸ Scene or the tune panel survives a character switch —
  // the same guarantee unloadAuthoredEnvironment gives a human's environment
  // pick.
  useEffect(() => {
    const check = () => {
      const { nodes, edges } = eventGraphState
      if (isAnyNodeOfTypeReachable('setBackdrop', nodes, edges)) return
      if (liveTuneStore.revertAuthored('backdrop')) {
        console.info('[App] reverted the blueprint-authored backdrop — no setBackdrop node is wired any more')
      }
    }
    const unsub = subscribeEventGraph(check)
    check()
    return unsub
  }, [])

  const handleAvatarLoaded = (info) => {
    setAvatarInfo(info)
    setLoaded(true)
    // An avatar is on screen — clear any error overlay still showing from a
    // character we switched away from. Closes the race where a superseded
    // instance set `error` during the switch's async window (before the active
    // id ref updated), which the per-error guard alone can't catch.
    setError(null)
    // Register the currently-loaded gestures/prefabs into the runtime now
    // that the avatar's globals (__FACE_CLIP_PLAYER__,
    // __REGISTER_CUSTOM_BODY_CLIP__) are available. The data was already
    // fetched by CustomAssetManager.init when the character changed (see
    // the character.id useEffect below) — this just connects it.
    const tryReg = () => {
      if (window.__FACE_CLIP_PLAYER__?._loaded && window.__REGISTER_CUSTOM_BODY_CLIP__) {
        CustomAssetManager.registerToRuntime()
      } else {
        setTimeout(tryReg, 300)
      }
    }
    tryReg()
  }

  // Initialise CustomAssetManager as soon as the active character is
  // known — DON'T wait for AvatarModel to finish loading. Uploads /
  // lists use the manager's internal _characterId; gating init on the
  // avatar caused uploads-before-avatar-ready to post to the default
  // character ("Upload failed: 404 character 'default' not found").
  useEffect(() => {
    if (!character?.id) return
    CustomAssetManager.ensureInit(character.id).catch(e => {
      console.warn('[App] CustomAssetManager init failed:', e?.message ?? e)
    })
  }, [character?.id])

  const handleError = (err) => {
    // Drop a stale failure for a character we've already switched away from.
    // The old AvatarModel instance's loadAll() rejects after unmount (the GLTF
    // load can't be cancelled under StrictMode) and would otherwise overwrite
    // the now-loaded new character with the old one's error overlay — the
    // intermittent "switch takes me back to the rigless character" bug.
    if (isStaleLoadError(err, activeCharIdRef.current)) {
      console.warn(`[App] ignoring stale load error for "${err.characterId}" (active is "${activeCharIdRef.current}")`)
      return
    }
    setError(err?.message || String(err))
  }

  // Unsaved-changes modal state. When the user clicks a character menu
  // entry while the current character has a dirty draft, we capture the
  // pending switch here and render <UnsavedChangesModal>. The actual
  // switch only commits on a Save (after sync succeeds) or Discard choice
  // — Cancel keeps us where we are. See docs/UX_PRINCIPLES.md.
  const [pendingSwitch, setPendingSwitch] = useState(null) // idOrChar | null
  const [modalBusy, setModalBusy] = useState(false)

  // Commit the actual switch — runs after the user has chosen Save (and
  // sync succeeded) or Discard (and the draft was cleared). Cancel never
  // calls this. Side effects in App.jsx only; BlueprintEditor picks up the
  // new activeCharId via prop.
  const commitSwitch = useCallback(async (idOrChar) => {
    const isObj = typeof idOrChar === 'object' && idOrChar !== null
    const id = isObj ? idOrChar.id : idOrChar

    // Acknowledge the click BEFORE the first await. Deliberately here and not in
    // handleSwitchCharacter: that one defers behind the unsaved-changes modal, so
    // setting it there would leave a phantom pending state when the user cancels.
    //
    // The chip menu calls us with an ID ONLY, so the human-readable label is read
    // back out of the list that menu rendered from (launcherCharsRef — the ref
    // rather than the state, since this callback has no deps). Nothing is
    // invented: an id we have no row for falls back to showing the id, which is
    // still the thing the user picked. Showing "char-b" when they clicked "CharB"
    // is a worse answer than showing "CharB", and we already have "CharB".
    const known = isObj ? idOrChar : launcherCharsRef.current.find(c => c.id === id)
    setSwitchingTo({ id, label: known?.label ?? known?.name ?? id })

    // Resolve the target character BEFORE committing anything (ENG-876).
    // Persisting the selection first left split-brain state when the
    // resolution failed (e.g. expired session → 401 on /api/characters):
    // the app stayed on the old character while localStorage already
    // pointed at the new one — nothing visibly happened, but the next
    // reload booted a different character ("only F5 refreshes the graph").
    // A failed switch is LOUD (App error overlay, which offers recovery)
    // and commits nothing.
    let char = idOrChar
    if (!isObj) {
      clearCharactersCache()
      let list
      try {
        list = await loadCharacters()
      } catch (e) {
        console.error('[App] commitSwitch failed:', e)
        setSwitchingTo(null)   // the error overlay owns the screen now
        setError(`Couldn't switch character: ${e?.message ?? e}`)
        return
      }
      char = list.find(c => c.id === id)
      if (!char) {
        console.warn('[App] commitSwitch: character not found in list:', id)
        setSwitchingTo(null)   // the error overlay owns the screen now
        setError(`Couldn't switch character: "${id}" not found on the server`)
        return
      }
    }

    setCurrentCharacterId(id)
    resetStorageKeys()
    setActiveCharacter(id)
    setDraftState(loadDraftFromLS())
    setDirtyDraft(getCharDraft())
    setDraftIsDirty(hasActiveDraft())

    characterRigRef.current = char.rigPath ?? null
    // Probe the TARGET character's /ogs bundle — unconditionally, on both the
    // id-only and the config-object path. This tags `char.renderer`, which is what
    // splatMode reads, so it must run against the character being switched TO.
    //
    // loadCharacters() cannot stand in for it: its enrichActiveWithPack resolves
    // "the active character" from localStorage, which still holds the previous id
    // at this point — setCurrentCharacterId runs after, so a switch that fails to
    // resolve commits nothing (ENG-876). Keep this call here and keep it
    // unconditional.
    await applyCharacterPack(char)
    pushRecentCharacterId(id, Date.now())
    setLoaded(false)
    setRenderBlocked(null)
    setCharacter(char)
    // Hand over: from here `character` + `loaded` describe the incoming character,
    // so the ordinary loading affordances take the story on without a gap. Cleared
    // LAST so there is no frame where neither state claims the screen.
    setSwitchingTo(null)
  }, [])

  // Open a character picked on the launcher. Reuses commitSwitch's config-object
  // path (no re-fetch — the launcher already holds the full record).
  //
  // pinCharacterUrlParam runs BEFORE commitSwitch on purpose: it pushes
  // ?character=<id> onto the URL, so setCurrentCharacterId's
  // reconcileCharacterUrlParam then finds a param that already matches and
  // no-ops instead of doing a second, redundant history write.
  // Open the launcher deliberately (AppMenu → "All characters…"). Strips
  // ?character= so the URL reflects "no character selected" and Back/Forward move
  // between the launcher and the character symmetrically (see the popstate
  // effect). `character` is left mounted, so returning is instant — no reload.
  const openLauncher = useCallback(() => {
    if (!launcherCharsRef.current.length) {
      console.warn('[App] launcher requested before the character list resolved')
      return
    }
    unpinCharacterUrlParam()
    setShowLauncher(true)
  }, [])

  // Delete a character from the launcher. The launcher gates this behind a
  // hold-to-confirm, so there is no second prompt here — a modal after a
  // deliberate 800ms hold is a question already answered.
  //
  // Same AAM path the error screen's Remove uses (ENG-921: characters live in the
  // asset-manager, not convorcher). A failure is LOUD and changes nothing.
  // Sharing change from the launcher's Properties panel (ENG-1028).
  //
  // Reloads the list from the server rather than patching the row in place: the
  // change can remove a character from this caller's OWN view (un-sharing one
  // an org-admin does not own), and a local patch would leave a card on screen
  // that the next request would not return. The server is the source of truth
  // for visibility — that is the whole feature.
  //
  // Throws on failure so CharacterProperties can show the server's message in
  // the panel where the click happened, rather than the app-level overlay.
  const setSharedFromLauncher = useCallback(async (slug, shared) => {
    await setCharacterSharing(slug, shared)
    clearCharactersCache()
    const list = await loadCharacters()
    writeCharList(list)
  }, [writeCharList])

  // Owner & visibility for the ACTIVE character, reachable from the menubar
  // (Character → Owner & visibility…) rather than only from the launcher. Same
  // panel either way — CharacterProperties is the one surface that explains a
  // character's provenance and visibility, and a second dialog saying the same
  // things differently is how the two drift apart.
  //
  // The row is re-fetched on open rather than read from launcherChars: the
  // menubar is reachable long after that list was loaded, and `shared`/`ownerId`
  // are exactly the fields another person may have changed since.
  const [ownerPanelChar, setOwnerPanelChar] = useState(null)
  const [ownerPanelError, setOwnerPanelError] = useState(null)

  const openOwnerPanel = useCallback(async (slug) => {
    if (!slug) return
    setOwnerPanelError(null)
    try {
      setOwnerPanelChar(await getCharacter(slug))
    } catch (e) {
      // Loud, not silent: an unopenable panel with no explanation reads as a
      // dead menu item.
      setOwnerPanelError(`Couldn't load "${slug}": ${e?.body?.error ?? e?.message ?? e}`)
    }
  }, [])

  const assignOwnerFor = useCallback(async (slug, userId, email) => {
    await assignCharacterOwner(slug, userId, email)
    clearCharactersCache()
    const [list, fresh] = await Promise.all([loadCharacters(), getCharacter(slug)])
    writeCharList(list)
    // Re-read the row so the panel reflects the new owner without closing —
    // the next thing the user does is usually make it private.
    setOwnerPanelChar(prev => (prev && prev.id === slug ? fresh : prev))
  }, [writeCharList])

  const removeFromLauncher = useCallback(async (char) => {
    const id = char?.id
    if (!id) return
    // Re-entry guard: the confirm dialog already gates a first click, but the
    // hold-to-confirm closes the instant the hold completes while the DELETE
    // itself keeps running for seconds server-side — exactly the window a
    // second click (or a re-fired handler) used to slip through and land a
    // second DELETE on an id already gone.
    if (pendingOpsRef.current[id]) return
    pendingOpsRef.current = { ...pendingOpsRef.current, [id]: 'deleting' }
    setPendingOps(p => ({ ...p, [id]: 'deleting' }))
    console.log('[App] launcher delete:', id)
    try {
      await deleteCharacter(id)
    } catch (e) {
      console.error('[App] launcher delete failed:', e)
      pendingOpsRef.current = omitKey(pendingOpsRef.current, id)
      setPendingOps(p => omitKey(p, id))
      await dialog.alert({
        title: 'Could not delete character',
        body: `“${id}” was not deleted: ${e?.message ?? e}`,
        variant: 'error',
      })
      return
    }

    // Re-read the list rather than splicing locally — the server is the source of
    // truth, and this also drops the deleted id from the rig-status probe.
    clearCharactersCache()
    let list
    try {
      list = await loadCharacters()
    } catch (e) {
      pendingOpsRef.current = omitKey(pendingOpsRef.current, id)
      setPendingOps(p => omitKey(p, id))
      setError(`Deleted “${id}”, but couldn't reload the character list: ${e?.message ?? e}`)
      return
    }

    writeCharList(list)
    pendingOpsRef.current = omitKey(pendingOpsRef.current, id)
    setPendingOps(p => omitKey(p, id))

    // Deleting the last character leaves the launcher with nothing to show; the
    // create-first empty state is the correct screen for that.
    if (!list.length) {
      setShowLauncher(false)
      setNoCharacters(true)
      return
    }

    // The launcher can be open OVER a loaded character (menu → All characters).
    // If that character was the one deleted, drop it so nothing downstream holds
    // a record the server no longer has.
    if (activeCharIdRef.current === id) {
      setCharacter(null)
      setLoaded(false)
      unpinCharacterUrlParam()
    }
  }, [writeCharList])

  // Non-destructive — no confirm, no hold — mirrors removeFromLauncher's
  // refresh flow minus the delete call and the confirm dialog. The new
  // character shows up in the refreshed launcher list; the app never
  // auto-opens it.
  const duplicateFromLauncher = useCallback(async (character) => {
    const id = character?.id
    if (!id) return
    // Google-Docs "Make a copy" pattern (ENG-1044 Task 14): ask for a name
    // BEFORE anything else. Cancel (Escape/backdrop/Cancel button, all
    // resolve null) means no request and no pendingOps entry — the
    // placeholder card must only ever appear once the user has committed.
    const name = await dialog.prompt({
      title: 'Duplicate character',
      initialValue: `${character.label || character.id} copy`,
      selectAll: true,
      confirmLabel: 'Duplicate',
    })
    if (name == null) return
    // Same re-entry guard as removeFromLauncher — duplicate is observed to take
    // ~13s on staging with the card's only feedback being the ⋯ button's own
    // busy state, which a second click via a different path (e.g. re-opening
    // the menu) could otherwise race past.
    if (pendingOpsRef.current[id]) return
    pendingOpsRef.current = { ...pendingOpsRef.current, [id]: 'duplicating' }
    setPendingOps(p => ({ ...p, [id]: 'duplicating' }))
    setPendingNames(p => ({ ...p, [id]: name }))
    try {
      await duplicateCharacter(id, name)
    } catch (err) {
      pendingOpsRef.current = omitKey(pendingOpsRef.current, id)
      setPendingOps(p => omitKey(p, id))
      setPendingNames(p => omitKey(p, id))
      await dialog.alert({
        title: 'Could not duplicate character',
        body: err?.message || String(err),
        variant: 'error',
      })
      return
    }
    clearCharactersCache()
    let list
    try {
      list = await loadCharacters()
    } catch (err) {
      pendingOpsRef.current = omitKey(pendingOpsRef.current, id)
      setPendingOps(p => omitKey(p, id))
      setPendingNames(p => omitKey(p, id))
      setError(`Duplicated, but couldn't reload the character list: ${err?.message ?? err}`)
      return
    }
    writeCharList(list)
    pendingOpsRef.current = omitKey(pendingOpsRef.current, id)
    setPendingOps(p => omitKey(p, id))
    setPendingNames(p => omitKey(p, id))
  }, [writeCharList])

  const openFromLauncher = useCallback(async (char) => {
    console.log('[App] launcher opened character:', char?.id)
    pinCharacterUrlParam(char.id)
    setShowLauncher(false)
    await commitSwitch(char)
  }, [commitSwitch])

  // Switch character in-place without a page reload. If the current
  // character has unsaved changes, defers the actual switch behind the
  // UnsavedChangesModal. Cancel in the modal truly cancels — no
  // localStorage writes, no menu repaint.
  const handleSwitchCharacter = useCallback(async (idOrChar) => {
    const isObj = typeof idOrChar === 'object' && idOrChar !== null
    const id = isObj ? idOrChar.id : idOrChar
    console.log('[App] handleSwitchCharacter:', id, isObj ? '(direct config)' : '(id only)')

    const draft = getCharDraft()
    if (hasActiveDraft() && draft?.id && draft.id !== id) {
      // Defer — modal will resolve the choice.
      setPendingSwitch(idOrChar)
      return
    }

    await commitSwitch(idOrChar)
  }, [commitSwitch])

  // Keep a list of characters around so the error screen can offer "switch to
  // another character" instead of an infinite reload loop on a broken one.
  // Also the deep-link boot's own fallback: the boot effect only reaches its
  // `writeCharList(list)` call if its OWN `loadCharacters()` succeeds — a
  // failure there sets `error` and skips straight past it, leaving the chip
  // and this error screen with no list at all. This effect runs on mount too
  // (error starts null) and on every later error, so it re-resolves that case.
  //
  // Never clobber a populated list with this fetch's result:
  //   - `!list.length` is this effect's OWN empty-result guard. Its job is
  //     SEEDING an empty list (boot failure, or first mount before boot
  //     resolves) — an empty result from ITS OWN fetch is a no-op by design,
  //     never a signal to blank an existing list.
  //   - the generation-token check below guards against every OTHER writer.
  //     `charListGen` is bumped by `writeCharList` on every character-list
  //     write, EMPTY OR NOT — deleting the last character is a legitimate
  //     fresh `[]`, and a "ref still empty" proxy can't distinguish that from
  //     "nobody's written yet" (it can't: both read as length 0). Capturing
  //     the generation before this fetch starts and comparing at resolve time
  //     catches that: any write that happened in between — in particular
  //     `removeCurrentCharacter`, the one flow that runs concurrently with a
  //     live `[error]`-effect instance (clearCharactersCache() + its own
  //     loadCharacters() + writeCharList(), all before it clears `error`) —
  //     bumps the token, so this fetch's OLDER resolution (its `cancelled`
  //     flag only flips on the NEXT `error` change, not on that concurrent
  //     write) is dropped regardless of what it resolved to.
  useEffect(() => {
    let cancelled = false
    const genAtStart = charListGen.current
    loadCharacters()
      .then(list => {
        if (cancelled) return
        if (!list || !list.length) return
        if (charListGen.current !== genAtStart) return
        writeCharList(list)
      })
      .catch(() => { /* error screen still offers Reload */ })
    return () => { cancelled = true }
  }, [error, writeCharList])

  // Delete the current (broken) character from the server, then switch to
  // another. Mirrors the delete flow in BlueprintEditor's character menu.
  const removeCurrentCharacter = useCallback(async () => {
    const id = character?.id
    if (!id) return
    if (!window.confirm(`Remove character "${id}"?\n\nThis deletes it from the server and cannot be undone.`)) return
    try {
      // ENG-921: characters live in the asset-manager, not convorcher. The old
      // bare `fetch('/api/characters/…')` hit convorcher's /api proxy — 404 since
      // ENG-579 removed those handlers — and carried no session cookie. The AAM
      // helper routes through apiFetch (credentials: 'include' + 401→refresh).
      await deleteCharacter(id)
    } catch (e) {
      console.error('[App] delete character failed:', e)
      window.alert(`Could not delete "${id}": ${e?.message ?? e}`)
      return
    }
    clearCharactersCache()
    const list = await loadCharacters()
    // Mirror the launcher flows (removeFromLauncher, duplicateFromLauncher,
    // etc.): write the fresh list directly rather than waiting on the
    // `[error]`-keyed effect to pick it up. That effect's own in-flight fetch
    // (started before this delete, against the stale pre-delete list) can
    // resolve AFTER this — see its comment — so this is the write that must
    // land, and going through writeCharList bumps charListGen so that later,
    // stale resolution is rejected even if `list` here is empty (the last
    // character just got deleted).
    writeCharList(list)
    const next = list.find(c => c.id !== id) ?? list[0]
    setError(null)
    setLoaded(false)
    if (next) await commitSwitch(next.id)
  }, [character?.id, commitSwitch, writeCharList])

  // Modal choice handler. Save → invoke the BlueprintEditor's saveToCloud
  // (exposed via window.__SYNC_TO_CLOUD__) and only commit the switch on
  // success. Discard → clear the draft, then commit. Cancel → close the
  // modal, no switch.
  const handleModalChoice = useCallback(async (choice) => {
    const pending = pendingSwitch
    if (!pending) return

    if (choice === 'cancel') {
      setPendingSwitch(null)
      return
    }

    if (choice === 'discard') {
      clearCharDraft()
      setDirtyDraft(null)
      setDraftIsDirty(false)
      setDraftState('none')
      setPendingSwitch(null)
      await commitSwitch(pending)
      return
    }

    if (choice === 'save') {
      const sync = typeof window !== 'undefined' ? window.__SYNC_TO_CLOUD__ : null
      if (typeof sync !== 'function') {
        console.error('[App] Save chosen but __SYNC_TO_CLOUD__ is not available')
        // Don't lose the user's work — keep modal open with an error.
        return
      }
      setModalBusy(true)
      try {
        const ok = await sync()
        if (!ok) {
          console.warn('[App] Save did not fully succeed — keeping modal open so user can retry or cancel')
          setModalBusy(false)
          return
        }
        // Sync marks the draft saved on success (draft data stays, dirty
        // flag flips false). Refresh local state to mirror that and commit
        // the deferred switch.
        setDirtyDraft(getCharDraft())
        setDraftIsDirty(false)
        setDraftState(loadDraftFromLS())
        setPendingSwitch(null)
        setModalBusy(false)
        await commitSwitch(pending)
      } catch (e) {
        console.error('[App] Save failed:', e)
        setModalBusy(false)
      }
    }
  }, [pendingSwitch, commitSwitch])

  // ── Docked-shell state ──────────────────────────────────────────────────────
  // Which outliner row feeds the inspector. Adapter rows also arm the transform
  // gizmo (sceneSelection); info rows only inspect.
  const [inspectedRowId, setInspectedRowId] = useState(null)
  // Rebuild the outliner on a slow tick: its inputs (THREE scene, graph module
  // refs) are deliberately not React state, so nothing re-renders when a bone
  // count changes. 1.5s matches the editor's debug poll cadence class — chrome,
  // not hot path.
  const [, setShellTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setShellTick(t => t + 1), 1500)
    return () => clearInterval(id)
  }, [])
  // Mirror of the editor's open surface (registry state) — decides whether the
  // full-viewport editor overlay is interactive or inert.
  const [editorSurface, setEditorSurface] = useState(null)
  useEffect(() => menubarCommands.subscribe(st => setEditorSurface(st.state.activeBP ?? null)), [])

  // App publishes its own namespace (ENG-1028). Ownership and visibility belong
  // to the character, not to the blueprint editor, so registering here keeps the
  // menu item working with no editor surface open — and avoids drilling a
  // handler through BlueprintEditor for something it has no part in.
  //
  // The command is only published when there IS an active character, so with
  // none the menubar renders it disabled with a reason instead of a dead click.
  useEffect(() => {
    if (!character?.id) return undefined
    return menubarCommands.register('character-ownership', {
      commands: { 'character.ownerVisibility': () => openOwnerPanel(character.id) },
    })
  }, [character?.id, openOwnerPanel])

  // ENG-1079. After a thumbnail write, drop the character-list cache and refetch
  // so the launcher card shows the new image, and merge thumbnailUrl onto the
  // active character (thumbnailUrl is not in the remount key, so no reload).
  const refreshAfterThumbnail = useCallback(async (slug) => {
    const list = await loadCharacters()
    writeCharList(list)
    const fresh = list.find(c => c.id === slug)
    if (fresh) setCharacter(c => (c && c.id === slug ? { ...c, thumbnailUrl: fresh.thumbnailUrl } : c))
  }, [writeCharList])

  // Manual capture (the menubar Character ▸ Capture launcher thumbnail item).
  // Published only when there IS a character, so the item disables with a reason
  // rather than dead-clicking. Loud on both outcomes — server writes are never
  // silent (success and failure both confirm through the dialog).
  const captureThumbnail = useCallback(async () => {
    const slug = character?.id
    if (!slug) return
    // Immediate, non-invasive feedback (ENG-1079): a viewport pill shows
    // "Capturing…" the instant they click, then "saved" or an error — no bare
    // wait, no modal.
    thumbnailCapture.capturing()
    try {
      await captureAndUploadThumbnail(slug)
      await refreshAfterThumbnail(slug)
      thumbnailCapture.saved()
    } catch (e) {
      console.error('[thumbnail] capture failed:', e)
      thumbnailCapture.error(e?.message ?? String(e))
    }
  }, [character?.id, refreshAfterThumbnail])

  useEffect(() => {
    if (!character?.id) return undefined
    return menubarCommands.register('character-thumbnail', {
      commands: { 'character.captureThumbnail': captureThumbnail },
    })
  }, [character?.id, captureThumbnail])

  // Auto-fallback (ENG-1079): a freshly loaded character with no thumbnail gets
  // ONE automatic capture, once the avatar is ready. Never overwrites a manual
  // one, never retries (per-slug guard), and fails SILENTLY — an automatic
  // action does not interrupt with a modal, and a read-only shared character the
  // caller can't write simply logs and moves on.
  const autoThumbAttempted = useRef(new Set())
  useEffect(() => {
    const slug = character?.id
    if (!shouldAutoCaptureThumbnail(
      { slug, hasThumbnail: !!character?.thumbnailUrl, ready: loaded },
      autoThumbAttempted.current,
    )) return undefined
    autoThumbAttempted.current.add(slug)
    // RETRY until the viewport actually produces pixels. `loaded` fires before
    // the scene finishes compositing (IBL/"Lighting ready" and the splat/hair
    // passes land after it), so a single deferred snapshot could read an empty
    // or half-built frame — which is why the first cut looked like it never
    // fired. Poll the capturer every 700ms (up to ~9s); upload the first real
    // frame. Silent: an automatic action never interrupts, and a read-only
    // shared character the caller can't write just logs.
    let cancelled = false
    let tries = 0
    let timer = null
    const attempt = async () => {
      if (cancelled) return
      const dataUrl = await captureLauncherThumbnailDataUrl()
      if (cancelled) return
      if (!dataUrl) {
        // ~20s window: a heavy splat (isaac-v6) can take several seconds after
        // `loaded` before its first frame paints; keep retrying until the
        // subject is actually on screen rather than saving a grid-only frame.
        if (++tries < 24) { timer = setTimeout(attempt, 800) }
        else { console.warn(`[thumbnail] auto-capture: "${slug}" viewport never produced a real frame`); thumbnailCapture.reset() }
        return
      }
      console.info(`[thumbnail] auto-capturing "${slug}"`)
      uploadThumbnailDataUrl(slug, dataUrl)
        .then(() => { if (!cancelled) { refreshAfterThumbnail(slug); thumbnailCapture.saved() } })
        // Auto failures stay quiet (a read-only shared character the caller
        // can't write shouldn't flash red on every load) — clear the pill.
        .catch(e => { console.warn('[thumbnail] auto-capture upload failed:', e?.message ?? e); thumbnailCapture.reset() })
    }
    // Same pill as the manual capture, per the ENG-1079 review (auto first-time
    // capture should look identical). Announced up front so the user sees the
    // work; saved()/reset() close it out.
    thumbnailCapture.capturing()
    timer = setTimeout(attempt, 1200)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      // Switched away mid-capture: clear a still-spinning pill, but never a
      // 'saved' one (that re-run is triggered by the thumbnail landing).
      if (getThumbnailCaptureState().phase === 'capturing') thumbnailCapture.reset()
    }
  }, [loaded, character?.id, character?.thumbnailUrl, refreshAfterThumbnail])
  // Closing the surface (the header ✕) while popped must not strand an empty
  // OS window — bring the (now content-less) portal home. popIn is a no-op
  // when nothing is popped.
  useEffect(() => { if (!editorSurface) popInEditor() }, [editorSurface, popInEditor])
  // Debug HUD is hidden by default — opt-in via ?debug=1 or localStorage debug=1
  const showDebugHud = useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('debug') === '1'
      || localStorage.getItem('debug') === '1'
  }, [])

  const showConnectionStatus = useMemo(() =>
    FORCE_SHOW
    || new URLSearchParams(window.location.search).get('debug') === '1'
    || localStorage.getItem('debug') === '1'
  , [])
  const wsColor = { disconnected: 'var(--m-danger)', connecting: 'var(--m-warn)', connected: 'var(--m-online)' }[wsState]

  // ── The app chrome ─────────────────────────────────────────────────────────
  // ONE menubar, mounted above every render branch below.
  //
  // This replaces the floating "Menu ▾" overlay that used to be pinned top-left
  // in each branch. That overlay only existed because the real menubar lived
  // inside BlueprintEditor and therefore vanished during a character load — so a
  // user stuck on a hung asset fetch had no way to switch character, load a
  // preset, or clear cache short of devtools surgery. Fixing the ownership
  // (src/components/AppMenuBar.jsx + src/lib/menubarCommands.js) removes the
  // reason for a second menu, so there is now exactly one.
  const activeRigStatus = useMemo(() => {
    if (!character) return null
    // Same precedence as classifyRigStatus(), but read off the signals App has
    // already resolved for the ACTIVE character rather than re-probing /ogs.
    if (streamChar) return 'stream'
    if (splatMode) return 'splat'
    if (rigMissing) return 'missing'
    return 'rig'
  }, [character, streamChar, splatMode, rigMissing])

  // The chip's DirtyBadge. `dirtyDraft.id === character.id` matters: a draft
  // belonging to a different character must not badge this one as unsaved.
  const chipDirtyState =
    draftState === 'stale-backend' ? 'stale-backend'
      : (draftIsDirty && dirtyDraft?.id === character?.id) ? 'dirty'
        : 'clean'

  // Recents order the chip's switch list the same way the launcher orders its
  // cards. Re-read when the active character changes, which is when the list moves.
  const chipRecents = useMemo(() => readRecentCharacters(), [character?.id])

  // The browser tab. index.html can only ship a static title, and the one it
  // shipped named a single character ("AvatarOS - Myra") — so every other
  // character wore Myra's name, and so did the launcher with nothing open at all.
  // Derived here instead, from state App already holds.
  useEffect(() => {
    document.title = formatDocumentTitle({
      characterLabel: character?.label ?? character?.id ?? null,
      surface: SURFACE_LABELS[editorSurface] ?? null,
      // stale-backend counts as unsaved: there ARE local edits, they just haven't
      // been reconciled with the backend yet.
      dirty: chipDirtyState === 'dirty' || chipDirtyState === 'stale-backend',
    })
  }, [character?.label, character?.id, editorSurface, chipDirtyState])

  // Who drives the camera right now — the blueprint's authored rig, or the
  // editor-camera fallback. Same signal Scene.jsx decides ownership with, so the
  // outliner cannot disagree with the viewport about it. Always-live (Wave 2B):
  // a rig in the blueprint owns the camera; a rig-less graph falls back to the
  // editor OrbitControls.
  const cameraRig = useCameraRigState(true)
  const cameraOwner = character ? (cameraRig === 'ok' ? 'rig' : 'editor') : null

  // ── Outliner rows — real scene data only (see outlinerTree.js) ─────────────
  // Recomputed every render; the shell tick above guarantees a floor cadence.
  // NOT useMemo: its inputs are module singletons a dep list can't see.
  const splatUrlForName = liveTuneStore.getState().environment.splatUrl
  const environmentName = splatUrlForName
    ? decodeURIComponent(String(splatUrlForName).split('/').pop() ?? '').split('?')[0] || null
    : null
  const outlinerAdapters = getAdapters()
  const outlinerRows = buildOutlinerRows({
    character,
    adapters: outlinerAdapters,
    scene: typeof window !== 'undefined' ? window.__AVATAR_SCENE__ ?? null : null,
    rigStatus: activeRigStatus,
    bodyGraph: graphState,
    faceGraph: faceGraphState,
    eventGraph: eventGraphState,
    environmentName,
    // .ply-only bake, but a compressed row with a retained .ply source
    // (splat_ply, ENG-1070) is bakeable — the server bakes the sibling. The
    // Build button disables (with the reason, outlinerTree.js) only when the
    // server's RESOLVED files map lists no .ply; an unresolved map reads as
    // unknown (null), which leaves the button enabled rather than flashing
    // disabled during the lookup.
    environmentBakeable: !splatUrlForName ? null
      : isBakeableUrl(splatUrlForName) ? true
        : (() => { const f = knownEnvironmentFiles(splatUrlForName); return f === undefined ? null : !!f?.splat_ply })(),
    dirtyState: chipDirtyState,
    cameraOwner,
    // Reads null until the controller is bound to a camera; the row then omits
    // location/look-at rather than reporting zeros (outlinerTree.js).
    cameraTransform: typeof window !== 'undefined'
      ? (window.__CAMERA_CONTROLLER__?.getTransform?.() ?? null) : null,
    cameraFov: liveTuneStore.getState().camera?.fov ?? null,
    cameraTargetMode: liveTuneStore.getState().camera?.target?.mode ?? null,
    // Drives the ground plane row's eye toggle (Wave 1D) — the store value
    // verbatim, so the outliner can never disagree with Scene.jsx about it.
    backdrop: liveTuneStore.getState().backdrop ?? null,
    // The navigation row's dot + meta (ENG-1050). window.__NAVMESH__ verbatim;
    // navmeshStatus.js owns what it MEANS, so the row and its chip agree.
    navmesh: typeof window !== 'undefined' ? window.__NAVMESH__ ?? null : null,
    voxels: typeof window !== 'undefined' ? window.__VOXELS__ ?? null : null,
    collision: typeof window !== 'undefined' ? window.__COLLISION__ ?? null : null,
    // The navigation rows' own properties: which debug layers are drawn, the
    // navmesh build params, and the collision draw style. All of it lived in the
    // retired "Build Navmesh" window (ENG-1050).
    navDebug: navSettingsStore.getState().show,
    navParams: navSettingsStore.getState().params,
    navStyle: navSettingsStore.getState().style,
    rigBoard: rigBoardSource,
    // The live ?rigconstrain weight — the Face rig row's own CONSTRAINT slider.
    // Read as a module ref (like rigBoardSource) so outlinerTree stays pure.
    rigConstrain,
    // The live mouth mix's weight — the Face rig row's other slider. Same module-ref
    // treatment, same reason.
    liveFace: liveFaceControls,
  }).map(r => (r.adapterId
    ? { ...r, dirty: outlinerAdapters.find(a => a.id === r.adapterId)?.dirty?.() ?? 'clean' }
    : r))

  const inspectedRow = outlinerRows.find(r => r.id === inspectedRowId) ?? null

  const selectOutlinerRow = useCallback((id) => {
    // One details panel: picking a scene row evicts a content-browser asset
    // selection (and vice versa — see assetInspectorHost).
    assetInspectorHost.clear()
    setInspectedRowId(id)
    const row = id ? outlinerRows.find(r => r.id === id) : null
    // Adapter rows arm the gizmo; anything else detaches it — one selection
    // concept, not two competing ones.
    if (row?.adapterId) sceneSelection.select(row.adapterId)
    else sceneSelection.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlinerRows])

  // The ground plane row's eye — the SAME liveTuneStore write path
  // LiveTunePanel's GROUND checkbox and SettingsWorkspace use, so all three
  // controls stay one state. The shell tick bump makes the eye repaint now
  // rather than on the next 1.5s outliner rebuild.
  const toggleOutlinerVisibility = useCallback((id) => {
    if (id !== 'groundPlane') return
    const on = liveTuneStore.getState().backdrop.groundEnabled
    liveTuneStore.setState({ backdrop: { groundEnabled: !on } })
    setShellTick(t => t + 1)
  }, [])

  // ── Navigation row properties (ENG-1050) ───────────────────────────────────
  // Every control the retired "Build Navmesh" window used to own. One handler:
  // the rows declare `controls`, DockedShell renders them, this performs them.
  // Each write ends with a tick bump so the control repaints now rather than on
  // the next 1.5s outliner rebuild (the pattern the ground-plane eye uses).
  const [rowActionNote, setRowActionNote] = useState(null)
  const handleRowControl = useCallback(async (rowId, controlId, value) => {
    const bump = () => setShellTick(t => t + 1)
    switch (controlId) {
      case 'show-navmesh': navSettingsStore.setDebug('navmesh', value); return bump()
      case 'show-voxels': navSettingsStore.setDebug('voxels', value); return bump()
      case 'show-collision': navSettingsStore.setDebug('collision', value); return bump()
      case 'show-capsule': navSettingsStore.setDebug('capsule', value); return bump()
      case 'show-walk-path': navSettingsStore.setDebug('walkPath', value); return bump()
      case 'agent-radius': navSettingsStore.setParams({ agentRadius: value }); return bump()
      case 'agent-height': navSettingsStore.setParams({ agentHeight: value }); return bump()
      case 'nav-cell': navSettingsStore.setParams({ navCell: value }); return bump()
      // The face rig's constraint weight. Writes the module singleton
      // OGSModel.driveSplatFace reads every frame, so the drag lands on the next
      // frame with no reload — which is the instrument the question needs, since
      // "how much of this does this character want?" is answered by watching a
      // face talk. `initial` is what the character loaded with (a typed
      // ?rigconstrain, else 1), so the reset can't be lost to one drag.
      // The live mouth mix (a DRIVE, so it is not what rig-constrain blends): how far
      // the A2F mouth block is pulled toward the bundle's trained references. Same
      // singleton-read-every-frame story, so the drag lands on the next frame — which
      // is the only way to judge it, since it only does anything while speech is
      // playing through it.
      case 'live-mouth': setLiveMouthWeight(value); return bump()
      case 'live-mouth-reset': setLiveMouthWeight(liveFaceControls.initial); return bump()
      case 'rig-constrain': setRigConstrain(value); return bump()
      case 'rig-constrain-reset': setRigConstrain(rigConstrain.initial); return bump()
      case 'collision-mode': navSettingsStore.setStyle({ collisionMode: value }); return bump()
      case 'collision-on-top': navSettingsStore.setStyle({ collisionOnTop: value }); return bump()
      case 'build-navmesh': {
        // Through the bake store (not requestNavBake directly) so the status
        // reporting flips to baking and polls — the same path the lighting bake
        // uses, and what makes the row read BAKING n%.
        const assetId = activeEnvAssetId()
        if (!assetId) {
          setRowActionNote({ tone: 'error', text: 'No environment loaded — nothing to bake a navmesh for.' })
          return
        }
        // The bake rasterizer is .ply-only, but a row serving a compressed
        // encoding (.spz after the SPZ migration) still bakes — the server
        // uses the source .ply its folder retains (splat_ply, ENG-1070).
        // Refuse only when there is NO .ply source anywhere, with the real
        // reason — otherwise the request 500s server-side and the row relays
        // "parse .ply: reading header: EOF", which explains nothing. (The
        // row's Build button is also disabled for this, but the Build menu
        // and any other route into this handler aren't.)
        const envUrl = liveTuneStore.getState().environment.splatUrl
        if (envUrl && !isBakeableUrl(envUrl)) {
          const ply = await environmentFileUrl(envUrl, 'splat_ply')
          if (!ply) {
            setRowActionNote({ tone: 'error', text: 'This environment has no .ply source — the bake can only rasterize .ply, and the server lists no .ply for it. Upload the .ply to build a navmesh.' })
            return
          }
        }
        setRowActionNote({ tone: 'ok', text: 'requesting…' })
        try {
          await startBake(assetId, navSettingsStore.getState().params, requestNavBake, 'navmesh')
          setRowActionNote({ tone: 'ok', text: 'Build requested.' })
        } catch (err) {
          // Loud failure (CLAUDE.md): the server-side carve is a deferred
          // software-WebGPU stage, so surface it instead of faking success.
          setRowActionNote({
            tone: 'error',
            text: `Server bake unavailable: ${String(err?.message ?? err)}. Bake locally: npm run bake-navmesh`,
          })
        }
        bump()
        return
      }
      default:
        // A control the rows declared and nobody performs would be a dead
        // switch — say so rather than swallowing the click.
        console.error(`[outliner] no handler for control "${controlId}" on row "${rowId}"`)
    }
  }, [])

  // The note belongs to the build that produced it, not to whatever row is
  // selected next.
  useEffect(() => { setRowActionNote(null) }, [inspectedRowId])

  // Build ▸ Build Navmesh points HERE now that the navmesh has no window of its
  // own: the menu selects the row, whose properties hold the params and the
  // Build button. One place to set them, so there is nothing to keep in sync.
  useEffect(() => menubarCommands.register('outliner-select', {
    commands: { 'outliner.select': (id) => selectOutlinerRow(id) },
  }), [selectOutlinerRow])

  const menubar = (
    <AppMenuBar
      character={character}
      switchingTo={switchingTo}
      characters={launcherChars}
      recents={chipRecents}
      rigStatus={activeRigStatus}
      rigStatusById={rigStatusById}
      loading={!!character && !loaded && !error}
      loadingStage={loadingStage}
      loadProgress={loadProgress}
      dirtyState={chipDirtyState}
      onSwitchCharacter={handleSwitchCharacter}
      onOpenLauncher={openLauncher}
      onOpenConversation={() => setConversationOpen(true)}
      onOpenAppearance={() => setAppearanceOpen(true)}
      onOpenShortcutHelp={() => setShortcutHelpOpen(true)}
      onPopOut={handlePopOut}
      onToggleTune={() => setTuneVisible(v => !v)}
      isTuneVisible={tuneVisible}
      onToggleAnim={() => setAnimVisible(v => !v)}
      isAnimVisible={animVisible}
      onToggleRigPreview={() => setRigVisible(v => !v)}
      isRigPreviewVisible={rigVisible}
      // One mode (Wave 2B): the app is always playing, so the readout is just
      // the projection — no LIVE/STANDARD suffix left to disagree about.
      viewLabel="PERSPECTIVE"
    />
  )

  // Panels the menubar opens. Mounted alongside it so they survive the same
  // branch changes — a conversation workspace must not close because a manifest
  // fetch failed behind it.
  //
  // The zero-size fixed wrapper is load-bearing: it puts these panels in a
  // stacking context ABOVE the menubar (6000). Their roots are position:fixed
  // full-screen takeovers with their own modest z-indexes (cv-root is 1200), so
  // without it the menubar paints over the workspace's topbar — exactly where its
  // tabs live — and every click on them lands on the menubar instead.
  // versionPanelStore is a module singleton (two doors: the rail button and the
  // assets-dock toolbar), so App mirrors it into render state.
  const [versionsOpen, setVersionsOpen] = useState(() => versionPanelStore.getState().open)
  useEffect(() => versionPanelStore.subscribe(() => setVersionsOpen(versionPanelStore.getState().open)), [])
  // Same shape for the material graph editor: a shell-level window, so it is
  // not owned by the collapsible assets dock and does not cover the content
  // browser it needs textures dragged out of.
  const [graphAsset, setGraphAsset] = useState(() => materialGraphStore.getState().asset)
  useEffect(() => materialGraphStore.subscribe(() => setGraphAsset(materialGraphStore.getState().asset)), [])

  const menubarPanels = (
    <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 6001 }}>
      <SettingsWorkspace open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
      {/* Version Control lives HERE, beside the other shell windows, not inside
          ContentBrowser where it used to be: its rail door has to work with the
          assets dock collapsed, and a panel mounted inside the dock cannot. */}
      {versionsOpen && character?.id && (
        <VersionPanel characterId={character.id} onClose={() => versionPanelStore.close()} />
      )}
      {graphAsset && (
        <MaterialGraphModal
          key={graphAsset.rawId}
          asset={graphAsset}
          onClose={() => materialGraphStore.close()}
        />
      )}
      <ShortcutHelp open={shortcutHelpOpen} onClose={() => setShortcutHelpOpen(false)} />
      {conversationOpen && (
        <Suspense fallback={null}>
          <ConversationWorkspace
            open
            initialTab={conversationTab}
            onTabChange={setConversationTab}
            characterName={character?.name ?? character?.id}
            slug={character?.id}
            onClose={() => setConversationOpen(false)}
            // Push the (just-patched) session node's config at the live client
            // so a workspace edit reaches the NEXT session without a reload —
            // the same normalization the node's own exec applies. Without this,
            // a story/brain change persisted fine but the running client never
            // heard about it until something else reconnected.
            applySession={() => {
              // liveSessionConfigFor, not a truthy-character check: CLEARING the
              // brain writes character:'' to the node, and skipping the apply
              // there left the running client conversing as the brain the user
              // had just removed. It resolves the same rendered-slug fallback
              // boot uses, so an empty persona still lands somewhere real.
              const cfg = liveSessionConfigFor(eventGraphState.nodes, character?.id)
              if (cfg) client.setSessionConfig(cfg)
            }}
          />
        </Suspense>
      )}
    </div>
  )

  /**
   * Every return branch below goes through this.
   *
   * The point is not tidiness — it is that `menubar` is the SAME element at the
   * SAME position in the SAME wrapper type in every branch, so React reconciles
   * it across a loading → loaded → error transition instead of unmounting and
   * remounting it. An early return that rendered its own tree is exactly what
   * used to blow the chrome away mid-load.
   *
   * The content row is `position: relative` so the absolutely-positioned
   * LoadingOverlay covers the row and NOT the menubar: the viewport is the only
   * thing that ever waits on a load, and the menus stay live while it does.
   */
  /**
   * Every return branch below goes through this. The docked chrome — menubar,
   * tool rail, outliner, inspector, assets dock, console strip — renders
   * IDENTICALLY whether or not a character is loaded (build spec); `children`
   * is only the viewport slot's content, which is the one region that reacts
   * to boot state.
   *
   * `editorOverlay` (main branch only) is the full-viewport blueprint editor:
   * an absolute cover over the whole body row, inert while no surface is open.
   * Branches that don't pass it leave the editor unmounted, so its menu items
   * disable with the reason inline.
   */
  // ── Editor surface as a window (Rebecca, 2026-08-06: every window should
  //    move like Unreal's). Docked = fills the viewport region (unchanged);
  //    floating = draggable/resizable above the chrome, dragged by the
  //    editor's own surface header; popped = the SAME editor instance
  //    portalled into a real OS window (useWindowPopOut — cross-monitor with
  //    no second boot, so it shows whatever surface is open). Only meaningful
  //    with a surface open: with none, the overlay is inert and never floats.
  const { win: editorWin, dragProps: editorDragProps } = useWindowDrag('editor-surface')
  const editorFloating = !!editorSurface && editorWin.mode === 'floating'
  // This surface renders its own frame (below) instead of FloatingWindow's, so
  // it does not get that component's placement — and persisted geometry is
  // untrusted: a float placed on a wide monitor paints off-screen when the app
  // reopens smaller, taking its header (the only drag handle) with it. Same
  // store-owned rule every other window goes through.
  useLayoutEffect(() => {
    if (!editorFloating) return
    placeOnOpen('editor-surface', { w: EDITOR_WINDOW_SIZE.w, h: EDITOR_WINDOW_SIZE.h })
  }, [editorFloating])
  const editorPopped = !!editorSurface && editorWin.mode === 'popped' && !!editorPopupBody
  const editorWindowFrame = {
    dragProps: editorDragProps,
    buttons: editorWin.mode === 'popped' ? (
      <WindowBarButton testId="window-popin-editor-surface" title="Bring back into the app" onClick={popInEditor}>⇥</WindowBarButton>
    ) : (
      <>
        {editorWin.mode === 'floating' ? (
          <WindowBarButton testId="window-dock-editor-surface" title="Dock — fill the viewport" onClick={() => windowStore.set('editor-surface', { mode: 'docked' })}>⇤</WindowBarButton>
        ) : (
          <WindowBarButton testId="window-float-editor-surface" title="Float — drag this editor anywhere" onClick={() => {
            // Persist the initial float geometry, not just the mode: the drag
            // handler and the corner grip read x/y/w/h from the STORE, so a
            // float that leaves them null would drag from 0 while rendering at
            // the ?? defaults below — the window would jump on first grab.
            // Geometry is the effect above: it runs the moment the mode lands,
            // including a first float, which used to be placed here at a
            // hardcoded 80,80 next to two other spellings of the same size.
            windowStore.set('editor-surface', { mode: 'floating' })
            windowStore.bringToFront('editor-surface')
          }}>❐</WindowBarButton>
        )}
        <WindowBarButton testId="window-popout-editor-surface" title="Open in its own OS window (drag it to another monitor)" onClick={handlePopOut}>⧉</WindowBarButton>
      </>
    ),
  }

  const shell = (children, editorOverlay = null) => (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      background: 'var(--m-bg)',
    }}>
      {menubar}
      {menubarPanels}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'row' }}>
        <ToolRail
          onOpenAppearance={() => setAppearanceOpen(true)}
          onOpenConversation={character ? () => setConversationOpen(true) : null}
          onOpenVersions={character ? () => versionPanelStore.toggle() : null}
        />
        <OutlinerPanel
          rows={outlinerRows}
          selectedRowId={inspectedRowId}
          onSelect={selectOutlinerRow}
          onOpenSurface={id => menubarCommands.run('editors.open', id)}
          onToggleVisibility={toggleOutlinerVisibility}
        />
        <ResizeHandle axis="x" storeKey="outlinerW" />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* The viewport is a drop target for environment assets — the same
              applyEnvironment() the content browser's context menu runs. */}
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <ViewportDropZone>{children}</ViewportDropZone>
            {/* Editor surfaces cover the VIEWPORT, and only the viewport.
                This overlay used to sit on the whole content row: with a surface
                open it covered the tool rail, outliner, inspector and assets dock
                too, so every click on them was swallowed and the shell looked
                dead. "Full-viewport editor" means the viewport region — every
                other panel stays live beside it.
                Inert unless a surface is open, so the headless editor (registry
                publication, cloud sync, wizard) never blocks the viewport.
                Popped: the same wrapper PORTALS into the OS window's document
                (useWindowPopOut). The editor DOES remount across the flip — a
                portal is a different fiber type at the same tree slot — which
                is what initialActiveBP={editorSurface} below exists to carry
                through it. What survives by construction is the module graph
                state: it lives in this document either way, so the popped
                window is a view of the one true copy, not a second one. */}
            {editorOverlay && editorPopped && createPortal(
              <div
                data-testid="editor-overlay"
                data-window-mode="popped"
                style={{
                  width: '100%', height: '100%', display: 'flex',
                  flexDirection: 'column', background: 'var(--m-bg)',
                  color: 'var(--m-fg)', position: 'relative',
                }}
              >
                {editorOverlay}
              </div>,
              editorPopupBody,
            )}
            {editorOverlay && !editorPopped && (
              <div
                data-testid="editor-overlay"
                data-window-mode={editorFloating ? 'floating' : 'docked'}
                style={editorFloating ? {
                  // Floating: a real window above the chrome (windowStore z),
                  // dragged by the editor's own surface header. The wrapper
                  // stays a child of the viewport slot — position:fixed
                  // escapes it geometrically, so nothing remounts on mode
                  // flips (ReactFlow keeps its viewport, collab keeps its
                  // socket).
                  position: 'fixed',
                  left: editorWin.x ?? 80, top: editorWin.y ?? 80,
                  // Float opens as a WINDOW smaller than the docked region (the
                  // rail/outliner/inspector leave the viewport slot well under
                  // this), so popping out visibly detaches rather than growing
                  // the editor — the user then drags/resizes from there.
                  width: editorWin.w ?? EDITOR_WINDOW_SIZE.w, height: editorWin.h ?? EDITOR_WINDOW_SIZE.h,
                  zIndex: editorWin.z, pointerEvents: 'auto',
                  display: 'flex', flexDirection: 'column',
                  border: '1px solid var(--m-line-hi)', borderRadius: 8,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.55)', overflow: 'hidden',
                  background: 'var(--m-bg)',
                } : {
                  position: 'absolute', inset: 0, zIndex: 40,
                  pointerEvents: editorSurface ? 'auto' : 'none',
                }}
                onMouseDown={editorFloating ? () => windowStore.bringToFront('editor-surface') : undefined}
              >
                {editorOverlay}
                {editorFloating && <WindowResizeGrips id="editor-surface" />}
              </div>
            )}
          </div>
          <AssetsDock>
            <ContentBrowser
              characterId={character?.id}
              shellInspector
              onCollapse={() => shellLayout.toggleDock()}
            />
          </AssetsDock>
        </div>
        {/* ENG-1046: the Generate-body-animation panel's own region, to the LEFT
            of the inspector — so the properties of the clip you are modifying
            stay in their usual place at the far right rather than shifting every
            time this panel opens. Brings its own resize handle and renders only
            while a generate session is docked, so it costs nothing when unused
            and never displaces the inspector. */}
        <GeneratePanel />
        <ResizeHandle axis="x" storeKey="inspectorW" invert />
        <InspectorPanel
          row={inspectedRow}
          characterLabel={character?.label ?? character?.id}
          onOpenSurface={id => menubarCommands.run('editors.open', id)}
          onOpenRigPreview={() => setRigVisible(true)}
          onRowControl={handleRowControl}
          actionNote={rowActionNote}
        />
        {/* ENG-1029: the editor agent, rightmost. Its blueprint-refresh split:
            editor closed → loadBlueprintFromDB reloads the module graph refs
            directly; editor open → the panel prompts and routes through the
            editor's own Load-from-Cloud command, whose confirm dialog protects
            unsaved local edits. */}
        <ResizeHandle axis="x" storeKey="agentW" invert />
        <AgentPanel
          character={character?.id}
          editorOpen={editorSurface || null}
          onAutoReload={() => loadBlueprintFromDB(character?.id)}
          onPromptReload={() => menubarCommands.run('file.loadFromCloud')}
        />
      </div>
      <ConsoleStrip
        wsState={wsState}
        statusText={character
          ? `${character.id}${activeRigStatus && activeRigStatus !== 'unknown' ? ` · ${activeRigStatus.toUpperCase()}` : ''}`
          : 'no character'}
      >
        <SignOutButton />
      </ConsoleStrip>
    </div>
  )

  const centered = (color, text) => (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--m-bg)', color, fontFamily: 'var(--m-font)', fontSize: 14 }}>
      {text}
    </div>
  )

  // Boot, before the destination is known. Deliberately the FIRST branch and
  // deliberately without shell chrome: every other branch below is a place the
  // app has decided to be, and painting the docked shell here only to replace it
  // with the launcher a moment later is the empty-editor flash this fixes.
  //
  // DialogHost rides along because boot can raise a dialog before it resolves —
  // the dead-?character= alert is awaited while this screen is up, so without a
  // host to render it the boot would hang on a dismissal that can never happen.
  if (booting) {
    return (
      <>
        <BootSplash />
        <DialogHost />
      </>
    )
  }

  // Splat mode — hold render until the isaac blueprint has hydrated so the
  // editor + face runtime both see the Facepalm -> Isaac Splat chain. The chrome
  // is up already: this branch used to return before the menu was even defined,
  // which made it the one boot state with no way out at all.
  if (!shippedBlueprintReady) {
    return shell(centered('var(--m-accent)', 'Loading Isaac blueprint…'))
  }

  // No characters on the server (fresh/empty) — render the explicit empty
  // state with a Create-Character action, not the error overlay. Creating one
  // switches to it via commitSwitch (which accepts the full config object).
  if (noCharacters) {
    return shell(
      <NoCharactersScreen
        onCreated={config => { setNoCharacters(false); commitSwitch(config) }}
      />
    )
  }

  // Characters exist but none is explicitly selected → the launcher. Checked
  // BEFORE the `!character` branch so Back from a loaded character returns here
  // rather than falling through to the still-set character.
  //
  // The menubar is intentionally NOT rendered here, and this is the one branch
  // where that's right: the launcher is a full-screen takeover whose entire job
  // is picking a character, so a chip that says "no character — pick one" and a
  // "Back to launcher" item pointing at the launcher you're already on would both
  // be noise. This is a different SCREEN, not the mid-load gap the menubar exists
  // to survive.
  if (showLauncher) {
    return (
      <>
        <CharacterLauncher
          characters={launcherChars}
          recents={readRecentCharacters()}
          statusById={rigStatusById}
          now={Date.now()}
          onOpen={openFromLauncher}
          onRemove={removeFromLauncher}
          onDuplicate={duplicateFromLauncher}
          pendingOps={pendingOps}
          pendingNames={pendingNames}
          onCreated={config => { setShowLauncher(false); commitSwitch(config) }}
          viewerId={me?.id}
          viewerEmail={me?.email}
          isOrgAdmin={hasOrgAdminRole(me?.roles)}
          loadShareImpact={getShareImpact}
          onSetShared={setSharedFromLauncher}
          onAssignOwner={assignOwnerFor}
        />
        <DialogHost />
      </>
    )
  }

  // that still needs to be visible, not a half-rendered avatar.
  // See docs/UX_PRINCIPLES.md ("Loud Failure").
  if (error) {
    // DialogHost must render here too, same reason as the !character branch
    // below: shell() always mounts the AssetsDock/ContentBrowser regardless of
    // which branch is active, and the content browser can raise a dialog of
    // its own (e.g. FolderTree's "Reserved folder name" refusal) — a rig that
    // 404s (or otherwise fails to load) lands here while the dock stays fully
    // usable. Without a host, that dialog is pushed and never painted, and the
    // refusal that's supposed to be LOUD is instead silent.
    return shell(
      <>
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--m-bg)', color: 'var(--m-danger)', fontFamily: 'var(--m-font)', fontSize: 14, padding: 32, textAlign: 'center' }}>
          <div style={{ maxWidth: 560 }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>⚠</div>
            <div style={{ marginBottom: 8, fontSize: 16, color: 'var(--m-danger)' }}>Couldn’t load this character</div>
            <div style={{ color: 'var(--m-fg-2)', fontSize: 13, marginBottom: 16, wordBreak: 'break-word', lineHeight: 1.5 }}>{error}</div>

            {/* Switch to another character — avoids the reload-into-the-same-broken-character loop. */}
            {(() => {
              const others = launcherChars.filter(c => c.id !== character?.id)
              if (!others.length) return null
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ color: 'var(--m-fg-3)', fontSize: 11, marginBottom: 8 }}>Switch to another character:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                    {others.map(c => (
                      <button
                        key={c.id}
                        onClick={() => {
                          // Recover deterministically. The in-place switch spawns
                          // overlapping async loads that race against this single
                          // shared `error` slot (badly, when the API is slow), so
                          // it intermittently lands back on the broken character.
                          // From a dead-end error screen there's no session state
                          // worth preserving: commit the choice as the source of
                          // truth (localStorage + ?character= reconcile) and boot
                          // fresh on it — one instance, no race.
                          setCurrentCharacterId(c.id)
                          window.location.reload()
                        }}
                        style={{ background: 'color-mix(in srgb, var(--m-accent) 15%, var(--m-bg))', color: 'color-mix(in srgb, var(--m-accent) 25%, var(--m-fg))', border: '1px solid color-mix(in srgb, var(--m-accent) 40%, transparent)', borderRadius: 6, padding: '8px 14px', fontFamily: 'var(--m-font)', fontSize: 12, cursor: 'pointer' }}
                      >
                        {c.label ?? c.id}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })()}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                onClick={removeCurrentCharacter}
                style={{ background: 'color-mix(in srgb, var(--m-danger) 18%, var(--m-bg))', color: 'color-mix(in srgb, var(--m-danger) 40%, var(--m-fg))', border: '1px solid color-mix(in srgb, var(--m-danger) 45%, transparent)', borderRadius: 6, padding: '8px 16px', fontFamily: 'var(--m-font)', fontSize: 12, cursor: 'pointer' }}
              >
                Remove “{character?.id ?? 'character'}”
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{ background: 'var(--m-panel-hi)', color: 'var(--m-fg-2)', border: '1px solid var(--m-line-hi)', borderRadius: 6, padding: '8px 16px', fontFamily: 'var(--m-font)', fontSize: 12, cursor: 'pointer' }}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
        <DialogHost />
      </>
    )
  }

  if (!character) {
    // DialogHost must render here too: the stale-character picker (and any
    // other boot-time dialog) is shown while character is still null, before
    // the main return below mounts. Without this the dialog is pushed but
    // never rendered, and the boot hangs awaiting a pick that can't happen.
    return shell(
      <>
        {centered('var(--m-fg-3)', 'Loading character…')}
        <DialogHost />
      </>
    )
  }

  return shell(
    <>
      {/* ── Viewport ── the one shell region that reacts to load state. */}

        {/* Connection status badge — visible when VITE_CONVORCHER_WS_URL=localhost or ?debug=1 */}
        {showConnectionStatus && <ConnectionStatus />}

        {/* Stale-backend banner — a draft was authored against a different
            backend than the one currently connected. Render Apply / Discard
            actions so the user resolves it explicitly instead of the draft
            silently merging (the bug that motivated this UX rule). */}
        {draftState === 'stale-backend' && getStaleBackendDraft() && (
          <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 30,
            background: 'color-mix(in srgb, var(--m-warn) 15%, var(--m-bg))', border: '1px solid var(--m-warn)', borderRadius: 6,
            padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 12,
            color: 'var(--m-warn)', fontFamily: 'var(--m-font)', fontSize: 12,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.63)',
          }}>
            <DirtyBadge state="stale-backend" size={10} />
            <span>
              Local draft for <strong style={{ color: 'color-mix(in srgb, var(--m-warn) 55%, var(--m-fg))' }}>{getStaleBackendDraft()?.id || 'character'}</strong> was authored against a different backend.
            </span>
            <button
              onClick={() => { applyStaleBackendDraft(); setDraftState('loaded'); setDirtyDraft(getCharDraft()) }}
              style={{ background: 'color-mix(in srgb, var(--m-warn) 13%, transparent)', color: 'var(--m-warn)', border: '1px solid var(--m-warn)', borderRadius: 4, padding: '3px 10px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}
            >
              Apply anyway
            </button>
            <button
              onClick={() => { clearStaleBackendDraft(); setDraftState('none') }}
              style={{ background: 'transparent', color: 'color-mix(in srgb, var(--m-warn) 67%, transparent)', border: '1px solid color-mix(in srgb, var(--m-warn) 33%, transparent)', borderRadius: 4, padding: '3px 10px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}
            >
              Discard
            </button>
          </div>
        )}

        {/* Unsaved-changes modal — blocks character switch until the user
            picks Save / Discard / Cancel. Cancel keeps us where we are; no
            partial state writes. */}
        <UnsavedChangesModal
          open={!!pendingSwitch}
          draft={dirtyDraft}
          server={character}
          pendingCharacterId={typeof pendingSwitch === 'object' && pendingSwitch ? pendingSwitch.id : pendingSwitch}
          onChoice={handleModalChoice}
          busy={modalBusy}
        />

        {/* The unsaved-changes indicator lives on the menubar's character chip
            now — next to the character name it describes, which is the UX
            proximity rule this floating pill broke. It sat in a global status
            position and duplicated a signal the chip already carries. */}
        {/* Loud rig-missing state — non-blocking so the content browser stays usable for uploads.
            onBundleFound heals a failed boot-time /ogs probe: re-apply the pack and re-render into splat mode. */}
        <CheckMissingRig
          character={character}
          active={!splatMode && !streamChar}
          onBundleFound={(rows) => applyCharacterPack(character, rows).then(() => setCharacter(c => ({ ...c })))}
        />
        {/* Unified avatar boot progress: downloads (ORT wasm, /ogs decoders,
            hair.gsh) then hair init, one continuous bar over the viewport. */}
        <AvatarLoadOverlay />
        {/* Texture tier cascade progress (ENG-757) — self-hides when idle */}
        <TextureUpgradeIndicator />
        {/* Launcher-thumbnail capture feedback (ENG-1079) — self-hides when idle */}
        <ThumbnailCaptureIndicator />
        {/* Head-tracking camera-permission gesture surface + live indicator
            (ENG-981) — self-hides when no headTrackedCamera node is armed. */}
        <HeadTrackIndicator />
        {/* Right-click "Walk here" phase pill (ENG-1050) — generation takes
            seconds, so the gesture always reports what it's doing. */}
        <WalkHereIndicator />
        {/* The viewport right-click menu's MARKUP. It lives out here in react-dom
            land; ViewportContextMenu (inside the Canvas) owns the gesture and the
            ground raycast and feeds it through viewportMenuStore — DOM returned
            from inside the Canvas is reconciled by R3F and crashes the app. */}
        <ViewportMenu />
        {/* Audio playing but zero A2F frames = frozen face. Name the failing
            service instead of letting it look like an avatar bug. */}
        <A2FWarningIndicator />
        {/* The camera is blueprint-authored: say so loudly when no rig is wired up,
            rather than leaving an unexplained editor-camera fallback. */}
        <CameraRigNotice />
        {/* OGS decoder fp16→fp32 downgrade warning (splat characters only) */}
        {splatMode && <OGSAssetWarningIndicator />}
        {/* Dev: anim-test panel (opt-in via ?anim=1) */}
        <AnimTestPanel visible={animVisible} />
        {/* Rig preview face-control board (opt-in via ?rig=1 or the node button) */}
        {rigVisible && <RigPreviewPanel onClose={() => setRigVisible(false)} />}
        {/* Dev: tuner (opt-in via ?tune=1 — self-gated inside the panel) */}
        <LiveTunePanel
          visible={tuneVisible}
          onBeforeReload={() => {
            // Nothing mode-shaped left to restore across a forced reload
            // (Wave 2B: liveView is gone); the key is still written so other
            // reload-state producers/consumers (activeBP) keep one channel.
            try { sessionStorage.setItem('aos-reload-state', JSON.stringify({})) } catch { /* ignore */ }
          }} />
        {/* The ?tune-gated OutlinerRail became the shell's permanent outliner +
            inspector + tool rail (DockedShell.jsx) — the gizmo picker is chrome
            now, not dev tooling. */}
        {/* Print String message overlay. The old inset cleared the overlay
            OutlinerRail, which no longer overlays the viewport. */}
        {printMessages.length > 0 && (
          <div style={{
            position: 'absolute', top: 12, left: 12, zIndex: 20,
            display: 'flex', flexDirection: 'column', gap: 4,
            pointerEvents: 'none',
          }}>
            {printMessages.map(msg => (
              <div key={msg.id} style={{
                color: msg.color,
                fontFamily: 'var(--m-mono)',
                fontSize: 13,
                fontWeight: 'bold',
                textShadow: '1px 1px 3px rgba(0,0,0,0.9)',
                background: 'rgba(0,0,0,0.45)',
                padding: '3px 8px',
                borderRadius: 3,
                borderLeft: `3px solid ${msg.color}`,
              }}>
                {msg.text}
              </div>
            ))}
          </div>
        )}
        <Canvas
          camera={{ position: [-0.5, 1.4, 6.5], fov: 45 }}
          dpr={[1, 1.5]}
          // Enable shadow maps (needed by ContactShadow in live mode). R3F
          // defaults to false; without this the ShadowMaterial plane renders
          // fully transparent because there's no shadow map to sample.
          //
          // "variance" = VSMShadowMap — soft, fast, and actually respects
          // `shadow-radius` on the light (PCFSoftShadowMap has a fixed 3x3
          // kernel and treats radius as a no-op).
          shadows="variance"
          // preserveDrawingBuffer is only needed when the offline bake tool
          // reads pixels back via canvas.toDataURL(). Gated on ?bake=1 so
          // regular users don't pay the perf cost.
          gl={typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('bake')
            ? { preserveDrawingBuffer: true } : undefined}
          style={{
            // --m-stage, not --m-bg-graph: the 3D stage stays DARK in both
            // Studio themes (gass .stu-stagecard); legacy palettes resolve
            // this token to their bgGraph, so nothing changes for them.
            background: 'var(--m-stage)',
            width: '100%',
            height: '100%',
          }}
        >
          {/* Composites the scene in fp16 and blits to the canvas, so the
              splat blend isn't rounded to 8 bits once per layer. Takes over
              the render loop (useFrame priority 1) when the capability is
              there; mounts nothing otherwise. */}
          <Fp16BlendTarget />
          {/* ENG-1079: registers the launcher-thumbnail capturer (gl/scene/camera
              live only inside the Canvas). Inert until a capture is requested. */}
          <LauncherThumbnailCapturer />
          <Scene
            splatUrl={splatUrl}
            streamChar={streamChar}
            /* Selecting the outliner's NAVMESH row draws the navigation graph —
               the existing debug overlay, previously reachable only from the
               Build ▸ Build Navmesh panel (ENG-1050). */
            navmeshSelected={inspectedRowId === 'navmesh'}
          />
          {/* ENG-561: live probe/capture placement markers while the Bake
              Lighting settings panel is open. */}
          <BakePreviewGizmos />
          {/* Rig+mesh avatar (rig + body/face animation). Skipped entirely in
              splat mode — a splat character renders ONLY the Isaac splat
              avatar, so we never pay for (or 404 on) a rig GLB / animations /
              materials. The face graph that drives the Facepalm -> Isaac Splat
              chain (normally ticked here in AvatarModel.useFrame) is ticked by
              OGSModel instead. */}
          {!splatMode && !streamChar && !rigMissing && (
            <AvatarModel
              key={character.rigPath ?? character.id}
              character={character}
              animationBuffer={animationBuffer}
              onLoaded={handleAvatarLoaded}
              onError={handleError}
              onStage={setLoadingStage}
            />
          )}
          {!splatMode && !streamChar && !rigMissing && character.faceMorphs && (
            // Same key as AvatarModel: a character switch must remount HeadModel
            // so its cached morph-mesh refs (meshesRef et al.) reset and it
            // re-discovers the NEW character's head meshes. Without this, an
            // in-place switch left HeadModel driving the previous character's
            // now-detached meshes — the new face never moved until a reload.
            <HeadModel
              key={`head:${character.rigPath ?? character.id}`}
              animationBuffer={animationBuffer}
              onLoaded={(info) => setAvatarInfo(prev => ({ ...prev, ...info }))}
            />
          )}
          {/* Splat mode — Isaac splat avatar inside the existing viewer,
              driven by the OGS node chain. Mounts in the
              host scene (not a separate overlay). */}
          {/* MultipartOGSModel = OGSModel + the body blueprint driving a part rig.
              The wrapper is inert without BOTH a registered part driver (the bundle
              ships rig.glb + a clip) and an authored body graph, so isaac / eyeline /
              head+teeth render exactly as before — see MultipartOGSModel.jsx. */}
          {splatMode && !streamChar && <MultipartOGSModel animationBuffer={animationBuffer} onReady={() => setLoaded(true)} onError={(e) => setError(e?.message || String(e))} onRenderBlocked={setRenderBlocked} />}
          {/* In-world OGS-streaming avatar (Switch Character). Server runs the
              inference; this lifts/renders it in the host scene via the shared
              SparkRenderer. Takes precedence over the GLB / on-device splat avatars. */}
          {streamChar && <StreamAvatarModel character={streamChar} animationBuffer={animationBuffer} onReady={() => { setLoaded(true); setLoadingStage(null) }} onStage={setLoadingStage} onError={(e) => setError(e?.message || String(e))} />}
        </Canvas>

        {/* Floating HDRI override dropdown — overlays the Canvas. Auto-hides
            when there are no baked HDRIs in /assets/environments/equirect/. */}
        <HDRISwitcherUI />

        {/* ENG-529: spinner pill shown while a gsplat downloads + parses.
            Without it the canvas just goes black until Spark's onLoad fires,
            which can take many seconds for a 100MB+ scan. */}
        <SplatLoadingOverlay />

        {/* Live readout for the keyboard-driven probe rig (diffuse ambient +
            reflection pair). Auto-hides unless the rig is mounted. */}
        <ProbeReadoutHUD />

        {/* ENG-561: lighting bake status + "Bake Lighting" button for the active
            environment. Self-gates on an environment actually being loaded —
            no environment, no chip. */}
        <LightingStatusChip />
        {/* Explains an EMPTY navigation overlay: selecting the navmesh row asks
            to see the graph, and a built one answers by drawing itself, so the
            chip only appears when there is nothing to draw (ENG-1050). */}
        <NavmeshStatusChip selected={inspectedRowId === 'navmesh'} />

        {/* ENG-561: Bake Lighting settings window (opens from the chip's Bake
            button); pairs with the in-scene BakePreviewGizmos. */}
        <BakeSettingsPanel />

        {/* ENG-591: Build → Build Navmesh settings (character capsule + cell);
            pairs with the green AgentCapsuleGizmo in the scene. */}

        {/* ENG-561: global bake completion toasts — fire even after the user
            switches environment/character (the per-env chip can't). */}
        <BakeNotifications />

        {/* The mic used to float here as a VoxyWidget pill at bottom:4rem —
            faded (voxy styles MUTED at opacity .45) on a dark viewport, which
            is why nobody could find it. It now lives in <ChatInputBar/> below,
            inside the text field it belongs to. */}

        {/* Loud-failure state for voice input: if the voxyConfig node fired
            but VoxyCore couldn't start (mic permission denied, no input
            device), show a visible pill where the mic widget would be —
            never silently render nothing (UX rule: loud failure). */}
        {/* Loud-failure state for audio output: suspended AudioContext =
            autoplay-blocked (mobile). Tapping anywhere resumes (AudioPlayer's
            gesture-rescue listeners); the pill exists so the user knows a tap
            is needed instead of thinking the avatar ignored them. */}
        {audioBlocked && (
          <div
            data-testid="audio-blocked-pill"
            onClick={() => audioPlayer.ensureRunning()}
            style={{
              position: 'absolute', bottom: '6.5rem', left: '50%', transform: 'translateX(-50%)', flexShrink: 0,
              padding: '6px 14px', borderRadius: 999,
              background: 'color-mix(in srgb, var(--m-panel) 90%, transparent)', border: '1px solid var(--m-warn)',
              color: 'var(--m-warn)', fontFamily: 'var(--m-font)', fontSize: 11,
              pointerEvents: 'auto', cursor: 'pointer',
            }}
          >
            tap to enable audio
          </div>
        )}

        {!voxyActive && voxyError && (
          <div
            data-testid="voxy-error-pill"
            title={`Voice input unavailable: ${voxyError.message}. Check the browser's microphone permission for this site, then reload.`}
            style={{
              position: 'absolute', bottom: '4rem', left: '50%', transform: 'translateX(-50%)', flexShrink: 0,
              padding: '6px 14px', borderRadius: 999,
              background: 'color-mix(in srgb, var(--m-panel) 90%, transparent)', border: '1px solid var(--m-danger)',
              color: 'var(--m-danger)', fontFamily: 'var(--m-font)', fontSize: 11,
              pointerEvents: 'auto', cursor: 'help',
            }}
          >
            {voxyError.code === 'mic-unavailable' ? 'mic unavailable — voice input off' : 'voice input failed to start'}
          </div>
        )}

        {/* Bottom bar — text input + the mic (ChatInputBar owns the swap:
            typing field when muted, waveform across the whole field while
            listening). Kept in live mode so the user can talk to the
            character. The WS dot and Sign out moved to the console strip. */}
        {/* The popped editor's way home is its own ⇥ pop-in button (and
            closing the OS window) — no dock-back affordance needed here. */}
        <ChatInputBar
          voxyActive={voxyActive}
          voxyError={voxyError}
        />

      {/* Loading overlay — INSIDE the viewport slot, so the rail, outliner,
          inspector and dock stay interactive while a character loads. That gap
          is the entire reason this shell exists. Suppressed when the rig is
          missing (CheckMissingRig owns that window). */}
      {/* `|| switchingTo` closes the same Doherty gap the chip does: `loaded` only
          goes false once commitSwitch has finished resolving the target over the
          network, so the viewport sat on the OUTGOING character until then. A
          switch that FAILS to resolve clears switchingTo and sets `error`, which
          this gate already defers to — the overlay is never left stranded.
          The label below prefers switchingTo for the same reason: `character` is
          still the OUTGOING character while a switch resolves, so it read
          "Loading Myra" on the way to somebody else. */}
      {(!loaded || switchingTo) && !error && !rigMissing && (
        renderBlocked ? (
          /* ENG-991: the load can never finish and we KNOW why — say it, with a
             way out, instead of spinning forever with the reason buried in the
             console (how eyeline presented). Not the terminal error screen: the
             editor stays usable behind this card, and OGSModel clears the state
             live the moment a working chain appears in the Face Blueprint. */
          <div
            data-testid="render-blocked-overlay"
            style={{
              position: 'absolute', inset: 0, zIndex: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{
              maxWidth: 520, padding: '18px 22px', pointerEvents: 'auto',
              background: 'color-mix(in srgb, var(--m-bg) 92%, transparent)',
              border: '1px solid var(--m-warn)', borderRadius: 8,
              fontFamily: 'var(--m-font)', color: 'var(--m-fg)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
            }}>
              <div style={{ fontSize: 13, color: 'var(--m-warn)', marginBottom: 8 }}>
                “{character.label ?? character.id}” can’t render
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--m-fg-2)', marginBottom: 14 }}>
                {renderBlocked}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => window.location.reload()}
                  style={{ background: 'var(--m-panel-hi)', color: 'var(--m-fg-2)', border: '1px solid var(--m-line-hi)', borderRadius: 5, padding: '6px 14px', fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}
                >
                  Reload
                </button>
                <span style={{ fontSize: 11, color: 'var(--m-fg-3)', alignSelf: 'center' }}>
                  or switch character from the top-left menu
                </span>
              </div>
            </div>
          </div>
        ) : (
          <LoadingOverlay label={switchingTo?.label ?? switchingTo?.id ?? character?.label ?? 'avatar'}>
            {loadingStage && (
              <div style={{
                marginTop: 8, fontSize: 12, opacity: 0.75,
                fontFamily: 'var(--m-mono)',
                letterSpacing: 0.2,
              }}>
                {loadingStage}
              </div>
            )}
          </LoadingOverlay>
        )
      )}

      {/* Debug HUD — hidden unless ?debug=1 or localStorage debug=1 */}
      {loaded && showDebugHud && (
        <div id="debug-hud">
          <div className="debug-label" style={{ marginBottom: 6 }}>Phase 1.5 — Convorcher</div>

          <div><span className="debug-label">Body</span> <span className="debug-value">{avatarInfo.bodyMeshLoaded ? '✓' : '✗'}</span></div>
          <div><span className="debug-label">Face</span> <span className="debug-value">{avatarInfo.faceMeshLoaded ? '✓' : '✗'}</span></div>
          <div><span className="debug-label">Morphs</span> <span className="debug-value">{avatarInfo.morphTargetCount}</span></div>
          <div><span className="debug-label">Bones</span> <span className="debug-value">{avatarInfo.skeletonBones}</span></div>

          <hr style={{ border: '1px solid var(--m-line-hi)', margin: '6px 0' }} />

          <div>
            <span className="debug-label">WS</span>{' '}
            <span className="debug-value" style={{ color: wsColor }}>{wsState}</span>
          </div>
          <div><span className="debug-label">Frames rx</span> <span className="debug-value">{frameStats.totalReceived}</span></div>
          <div><span className="debug-label">Buffered</span> <span className="debug-value">{frameStats.buffered}</span></div>
          <div><span className="debug-label">Played</span> <span className="debug-value">{frameStats.played}</span></div>
          <div><span className="debug-label">Sentence</span> <span className="debug-value">{frameStats.sentenceNum}</span></div>

          <hr style={{ border: '1px solid var(--m-line-hi)', margin: '6px 0' }} />
          <div style={{ fontSize: 10, color: 'var(--m-fg-3)', lineHeight: 1.6 }}>
            Use InputKey nodes in Blueprint
          </div>
        </div>
      )}

      {/* Owner & visibility for the active character (ENG-1028), opened from
          Character → Owner & visibility…. Mounted here rather than inside the
          editor so it works with no surface open, the same reason the creation
          wizard is. */}
      {ownerPanelChar && (
        <CharacterProperties
          character={ownerPanelChar}
          now={Date.now()}
          viewerId={me?.id}
          viewerEmail={me?.email}
          isOrgAdmin={hasOrgAdminRole(me?.roles)}
          loadImpact={getShareImpact}
          onSetShared={async (slug, shared) => {
            await setSharedFromLauncher(slug, shared)
            setOwnerPanelChar(await getCharacter(slug))
          }}
          onAssignOwner={assignOwnerFor}
          onClose={() => setOwnerPanelChar(null)}
        />
      )}
      {ownerPanelError && (
        <div style={{
          position: 'fixed', bottom: 20, left: 20, zIndex: 10000,
          background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
          borderRadius: 6, padding: '10px 14px', fontFamily: 'var(--m-font)',
          fontSize: 11, color: 'var(--m-danger)', maxWidth: 420,
        }}>{ownerPanelError}</div>
      )}

      <DialogHost />
    </>,

    /* editorOverlay — the full-viewport blueprint editor. Mounted (headless)
       even with no surface open, so the menubar's editor commands and the
       unsaved-changes modal's __SYNC_TO_CLOUD__ stay live. While popped out
       it stays mounted HERE too — the OS window only hosts its portalled
       DOM, so the one true instance (and its module graph state) never
       leaves this document. */
    (
      <React.Profiler id="BlueprintEditor" onRender={(id, phase, actualDuration) => {
        if (!globalThis.__PERF_TRACE__ || actualDuration < 1) return
        performance.measure(`aos.react.${id}.${phase}`,
          { start: performance.now() - actualDuration, duration: actualDuration })
      }}>
        <BlueprintEditor
          // editorSurface first: moving the editor into/out of the pop-out
          // portal REMOUNTS it (a portal is a different fiber type at the same
          // tree slot, so React can't keep the instance). Module graph state
          // survives by construction; this prop is what carries the OPEN
          // SURFACE across the flip — without it the remount resets to null,
          // the registry republishes "no surface", and the pop-out
          // self-closes before it ever paints.
          initialActiveBP={editorSurface ?? reloadRestore?.activeBP ?? null}
          onSwitchCharacter={handleSwitchCharacter}
          activeCharId={character?.id}
          windowFrame={editorWindowFrame}
        />
      </React.Profiler>
    )
  )
}
